import React, { useRef, useEffect, useState, useMemo, useContext, createContext, useCallback } from 'react';

var classnames = {exports: {}};

/*!
  Copyright (c) 2018 Jed Watson.
  Licensed under the MIT License (MIT), see
  http://jedwatson.github.io/classnames
*/

(function (module) {
/* global define */

(function () {

	var hasOwn = {}.hasOwnProperty;

	function classNames() {
		var classes = [];

		for (var i = 0; i < arguments.length; i++) {
			var arg = arguments[i];
			if (!arg) continue;

			var argType = typeof arg;

			if (argType === 'string' || argType === 'number') {
				classes.push(arg);
			} else if (Array.isArray(arg)) {
				if (arg.length) {
					var inner = classNames.apply(null, arg);
					if (inner) {
						classes.push(inner);
					}
				}
			} else if (argType === 'object') {
				if (arg.toString === Object.prototype.toString) {
					for (var key in arg) {
						if (hasOwn.call(arg, key) && arg[key]) {
							classes.push(key);
						}
					}
				} else {
					classes.push(arg.toString());
				}
			}
		}

		return classes.join(' ');
	}

	if (module.exports) {
		classNames.default = classNames;
		module.exports = classNames;
	} else {
		window.classNames = classNames;
	}
}());
}(classnames));

var cx = classnames.exports;

const useClickOutside = (handler, dependencies) => {
    const containerRef = useRef(null);
    useEffect(() => {
        const handleMouseClick = (e) => {
            if (containerRef.current && containerRef.current.contains(e.target)) {
                // inside click
                return;
            }
            // outside click
            handler(e);
        };
        document.addEventListener("click", handleMouseClick, true);
        return () => {
            document.removeEventListener("click", handleMouseClick, true);
        };
    }, [dependencies, handler]);
    return containerRef;
};
const useMoveOutside = (handler) => {
    const containerRef = useRef(null);
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (containerRef.current && containerRef.current.contains(e.target)) {
                // inside click
                return;
            }
            // outside click
            handler(e);
        };
        document.addEventListener("mousemove", handleMouseMove, true);
        return () => {
            document.removeEventListener("mousemove", handleMouseMove, true);
        };
    }, [handler]);
    return containerRef;
};
const uniqueId = (prefix) => {
    return `${prefix || "id"}-${window.crypto
        .getRandomValues(new Uint32Array(1))[0]
        .toString(36)
        .substring(2, 16)}`;
};
const useFocus = () => {
    const inputRef = useRef(null);
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);
    return { inputRef };
};
const isMobileDevice = () => {
    return document.body.offsetWidth < 769;
};
const isTabletDevice = () => {
    return document.body.offsetWidth >= 769 && document.body.offsetWidth < 1200;
};
const chunkArray = (array, size) => {
    const results = [];
    while (array.length) {
        results.push(array.splice(0, size));
    }
    return results;
};
const compareArray = (array1, array2) => {
    return (array1.length === array2.length &&
        array1.every((value, index) => value === array2[index]));
};
const getStringDomLength = (s, fontSize = 14) => {
    return s.length * fontSize;
};
const createDataAttributeObject = (dataAttribute) => {
    const dataAttribs = {};
    Object.keys(dataAttribute).forEach(key => {
        dataAttribs[`data-${key}`] = dataAttribute[key];
    });
    return dataAttribs;
};

const Icon = ({ name, small, onClick, base, active, disabled, className, style, dataAttributes }) => (React.createElement("i", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), role: "presentation", onKeyPress: onClick, onClick: onClick, className: cx("icon", className, `icon-${name}`, { small: small === true }, { "align-baseline": base }, { disabled }, { active }), style: style }));

const Tag = ({ children, icon, big, className, severity, style, dataAttributes }) => {
    const withIcon = typeof icon === "string" ? React.createElement(Icon, { name: icon }) : icon;
    const severityClass = severity ? `severity-${severity}` : null;
    const clName = cx("tag", severityClass, className, { big });
    return (React.createElement("div", null,
        React.createElement("kbd", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), style: style, className: clName },
            withIcon,
            children)));
};

const LOCAL_STORAGE_NOTIFICATIONS_KEY = "notifications";
const LOCAL_STORAGE_THEME_KEY = "theme";
const localStorageKey = (key, id, user) => `${id || "gcc-eds"}-${user ? `${user.firstName}.${user.lastName}` : "."}-${key}`;
const ApplicationContext = React.createContext(undefined);
const ApplicationContextProvider = ({ user, appId, theme, forcedTheme, skipPersistTheme, children, menuDefaultClosed }) => {
    // Theme
    const getPersistedTheme = () => window.localStorage.getItem(localStorageKey(LOCAL_STORAGE_THEME_KEY, appId, user));
    const getTheme = () => theme && (forcedTheme || skipPersistTheme) ? theme : getPersistedTheme() || theme || "dark";
    const [menuVisible, setMenuVisible] = useState(menuDefaultClosed ? false : !isMobileDevice());
    const [globalNavigationVisible, setGlobalNavigationVisible] = useState(false);
    const [settingsVisible, setSettingsVisible] = useState(false);
    const [currentTheme, setTheme] = useState(getTheme);
    const [notificationsVisible, setNotificationsVisible] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [newNotifications, setNewNotifications] = useState(0);
    const persistTimeoutRef = useRef(undefined);
    persistTimeoutRef.current = undefined;
    const userId = useMemo(() => (user ? user.lastName || "" + user.firstName || "" : ""), [user]);
    const [bannerNotifications, setBannerNotifications] = useState([]);
    useEffect(() => {
        setTheme(theme && (forcedTheme || skipPersistTheme)
            ? theme
            : window.localStorage.getItem(localStorageKey(LOCAL_STORAGE_THEME_KEY, appId, user)) ||
                theme ||
                "dark");
    }, [appId, forcedTheme, skipPersistTheme, theme, user, userId]);
    useEffect(() => {
        const persistTheme = (t) => {
            window.localStorage.setItem(localStorageKey(LOCAL_STORAGE_THEME_KEY, appId, user), t);
        };
        if (!document.body.classList.contains(currentTheme)) {
            document.body.classList.remove("light");
            document.body.classList.remove("dark");
            document.body.classList.add(currentTheme);
        }
        if (!skipPersistTheme) {
            persistTheme(currentTheme);
        }
    }, [appId, currentTheme, skipPersistTheme, user]);
    // Theme
    const toggleTheme = () => {
        setTheme(currentTheme === "light" ? "dark" : "light");
    };
    // Settings
    const toggleSettings = () => {
        setSettingsVisible(!settingsVisible);
    };
    // Menu
    const toggleMenu = () => {
        setMenuVisible(!menuVisible);
    };
    // GlobalNavigation
    const toggleGlobalNavigation = () => {
        setGlobalNavigationVisible(!globalNavigationVisible);
    };
    // Notifications
    const nofNewNotifications = (items) => items.filter((i) => i.new && !i.instant && i.persist).length;
    useEffect(() => {
        const getPersistedNotifications = () => {
            const items = window.localStorage.getItem(localStorageKey(LOCAL_STORAGE_NOTIFICATIONS_KEY, appId, user));
            if (items) {
                return JSON.parse(items);
            }
            return [];
        };
        const items = getPersistedNotifications();
        setNotifications(notifications => [
            ...notifications,
            ...items.filter(i => notifications.find(n => n.id === i.id) === undefined)
        ]);
    }, [appId, user, userId]);
    useEffect(() => {
        const persistNotifications = (items) => {
            if (persistTimeoutRef.current) {
                window.clearTimeout(persistTimeoutRef.current);
            }
            persistTimeoutRef.current = window.setTimeout(() => {
                window.localStorage.setItem(localStorageKey(LOCAL_STORAGE_NOTIFICATIONS_KEY, appId, user), JSON.stringify(items.filter((n) => !n.instant)));
                persistTimeoutRef.current = undefined;
            }, 2000);
        };
        setNewNotifications(nofNewNotifications(notifications));
        persistNotifications(notifications.filter((n) => {
            return n.persist === true;
        }));
    }, [appId, notifications, user]);
    const toggleNotifications = () => {
        setNotificationsVisible(!notificationsVisible);
    };
    const clearNewNotifications = async () => {
        setNotifications(notifications => notifications.map((n) => ({ ...n, new: false })));
    };
    const clearActiveNotifications = async () => {
        setNotifications(notifications => notifications.map((n) => ({ ...n, active: false })));
    };
    const clearNotification = (id) => {
        setNotifications(notifications => notifications.map((n) => {
            if (n.id !== id) {
                return n;
            }
            if (n.timerId) {
                window.clearTimeout(n.timerId);
            }
            return { ...n, new: false, active: false, timerId: undefined };
        }));
    };
    const deleteNotification = (id) => {
        setNotifications(notifications => notifications.filter((n) => n.id !== id));
    };
    const deleteBannerNotification = (id) => {
        setBannerNotifications(previous => previous.filter((n) => n.id !== id));
    };
    const autoHideNotification = (id) => {
        setNotifications(notifications => notifications.map((n) => n.id !== id ? n : { ...n, active: false, timerId: undefined }));
    };
    const createSilentNotification = (title, description, onClick, timestamp, newItem, dataAttributes) => {
        const notificationId = uniqueId("ntf");
        const newNotification = {
            id: notificationId,
            type: "default",
            icon: undefined,
            title,
            description,
            dataAttributes,
            onClick,
            timestamp: timestamp || new Date(),
            new: newItem || false,
            active: false,
            stripeColor: "gray",
            instant: false,
            timerId: undefined,
            persist: false
        };
        setNotifications(notifications => [newNotification, ...notifications]);
    };
    const createNotification = (title, description, type, icon, timeout, instant, stripeColor, onClick, dataAttributes, persist) => {
        const notificationId = uniqueId("ntf");
        const newNotification = {
            id: notificationId,
            type: type || "default",
            icon,
            title,
            description,
            dataAttributes,
            onClick,
            timestamp: new Date(),
            new: true,
            active: true,
            stripeColor: stripeColor || "gray",
            instant: instant === true,
            timerId: timeout !== null && timeout !== 0
                ? window.setTimeout(() => autoHideNotification(notificationId), timeout || 10000)
                : undefined,
            persist: persist === undefined ? true : persist
        };
        setNotifications(notifications => [newNotification, ...notifications]);
        if (notificationsVisible) {
            setNotificationsVisible(false);
        }
        if (settingsVisible) {
            setSettingsVisible(false);
        }
        return notificationId;
    };
    const createInstantNotification = (title, description, type, icon, timeout, onClick, dataAttributes) => createNotification(title, description, type, icon, timeout, true, "gray", onClick, dataAttributes);
    const createBannerNotification = (title, description, icon, stripeColor, onClick, dataAttributes) => {
        const notificationId = uniqueId("bNtf");
        const newBanner = {
            id: notificationId,
            icon,
            title,
            description,
            stripeColor: stripeColor || "gray",
            onClick,
            dataAttributes
        };
        setBannerNotifications(bannerNotifications => [newBanner, ...bannerNotifications]);
        return notificationId;
    };
    return (React.createElement(ApplicationContext.Provider, { value: {
            user,
            theme: {
                current: currentTheme,
                toggle: toggleTheme,
                forced: forcedTheme !== undefined ? forcedTheme : false
            },
            menu: { visible: menuVisible, toggle: toggleMenu },
            globalNavigation: { visible: globalNavigationVisible, toggle: toggleGlobalNavigation },
            settings: {
                visible: settingsVisible,
                toggle: toggleSettings,
                setVisible: setSettingsVisible
            },
            notifications: {
                visible: notificationsVisible,
                setVisible: setNotificationsVisible,
                toggle: toggleNotifications,
                items: notifications,
                new: newNotifications,
                clearNew: clearNewNotifications,
                clearActive: clearActiveNotifications,
                create: createNotification,
                createInstant: createInstantNotification,
                createSilent: createSilentNotification,
                clear: clearNotification,
                delete: deleteNotification
            },
            bannerNotifications: {
                items: bannerNotifications,
                create: createBannerNotification,
                delete: deleteBannerNotification
            }
        } }, children));
};

const useAppContext = () => {
    const context = useContext(ApplicationContext);
    if (context === undefined) {
        throw new Error("Application context must be used within a Provider");
    }
    return context;
};

const toggleNavigation = () => {
    // The timeout is a workaround for the charts.
    setTimeout(() => {
        // eslint-disable-next-line no-undef
        const event = new CustomEvent("toggleNavigation");
        window.dispatchEvent(event);
    }, 320);
};
const toggleTiles = () => {
    // The timeout is a workaround for the charts.
    setTimeout(() => {
        // eslint-disable-next-line no-undef
        const event = new CustomEvent("toggleNavigation");
        window.dispatchEvent(event);
    }, 320);
};
const toggleSettings = () => {
    // eslint-disable-next-line no-undef
    const event = new CustomEvent("toggleSettings");
    window.dispatchEvent(event);
};
const toggleNotifications = () => {
    // eslint-disable-next-line no-undef
    const event = new CustomEvent("toggleNotifications");
    window.dispatchEvent(event);
};

const SysBar = ({ applicationName, acronym, onBrandClick, onBrandKeyDown, appBarItems, iconName, globalNavigationIcon, hideNotificationBell, hideGlobalNavigation }) => {
    const { user, settings, notifications, globalNavigation } = useAppContext();
    const settingsIconPressed = async () => {
        await notifications.clearActive();
        if (notifications.visible) {
            notifications.toggle();
            notifications.clearNew();
        }
        if (globalNavigation.visible) {
            globalNavigation.toggle();
        }
        settings.toggle();
        toggleSettings();
    };
    const notificationIconPressed = () => {
        if (notifications.visible) {
            notifications.clearNew();
        }
        else {
            notifications.clearActive();
        }
        if (settings.visible) {
            settings.toggle();
        }
        if (globalNavigation.visible) {
            globalNavigation.toggle();
        }
        notifications.toggle();
        toggleNotifications();
    };
    const globalNavigationIconPressed = async () => {
        await notifications.clearActive();
        if (notifications.visible) {
            notifications.toggle();
            notifications.clearNew();
        }
        if (settings.visible) {
            settings.toggle();
        }
        globalNavigation.toggle();
        toggleNotifications();
    };
    const logIndicator = () => {
        return (React.createElement("div", { role: "button", onKeyDown: notificationIconPressed, onClick: notificationIconPressed, className: cx("item notification-log-trigger", {
                "has-notification": notifications.new
            }), tabIndex: 0 },
            React.createElement("span", { className: "notification-log-number" }, notifications.new || undefined),
            React.createElement(Icon, { name: "notification" }),
            React.createElement(Icon, { name: "notification-ring" })));
    };
    const settingsToggleButton = () => user && (React.createElement("div", { className: "item hover" },
        React.createElement("div", { className: "btn-no-border", role: "button", onKeyDown: settingsIconPressed, onClick: settingsIconPressed, tabIndex: 0 },
            user.profilePicture ? (React.createElement("img", { src: user.profilePicture, className: "profile-picture", title: "Profile picture" })) : (React.createElement(Icon, { name: iconName || "profile" })),
            user && (React.createElement("span", { className: "username" }, `${user.firstName}${user.lastName ? ` ${user.lastName}` : ""}`)))));
    const globalNavigationToggleButton = () => {
        return (React.createElement("div", { className: "item hover" },
            React.createElement("div", { className: "btn-no-border", role: "button", onKeyDown: globalNavigationIconPressed, onClick: globalNavigationIconPressed, tabIndex: 0 },
                React.createElement(Icon, { name: globalNavigationIcon || "app-launcher" }))));
    };
    return (React.createElement("header", { className: "sysbar" },
        React.createElement("div", { className: "items-container" },
            React.createElement("div", { className: "item", role: "link", onKeyDown: e => onBrandKeyDown && onBrandKeyDown(e), onClick: onBrandClick, tabIndex: 0, style: { cursor: onBrandClick ? "pointer" : "default" } },
                React.createElement(Icon, { name: "econ" }),
                React.createElement("span", { className: "product" }, applicationName),
                React.createElement("span", { className: "acronym" }, acronym))),
        React.createElement("div", { className: "items-container" },
            appBarItems,
            !hideGlobalNavigation && globalNavigationToggleButton(),
            !hideNotificationBell && logIndicator(),
            settingsToggleButton())));
};

const Row = ({ children, className, style, contentRef, dataAttributes }) => {
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), ref: contentRef, className: cx("row", className), style: style }, children));
};

const Column = ({ sm, md, lg, xl, asContainer, children, className, style, contentRef, dataAttributes }) => (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), ref: contentRef, className: cx("column", { container: asContainer }, {
        [`sm-${sm}`]: sm,
        [`md-${md}`]: md,
        [`lg-${lg}`]: lg,
        [`xl-${xl}`]: xl
    }, className), style: style }, children));

const Switch = ({ onLabel = "On", offLabel = "Off", value, disabled, onChange, className, style, dataAttributes }) => {
    const id = uniqueId();
    return (React.createElement("label", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), htmlFor: id, id: id, className: cx("switch", className), style: style, onClick: () => !disabled && onChange(!value) },
        React.createElement("input", { type: "checkbox", checked: value, disabled: disabled, onChange: () => { } }),
        React.createElement("i", { className: "ball" }),
        React.createElement("span", { className: "checkbox-label", "data-enabled": onLabel, "data-disabled": offLabel })));
};

const Button = ({ primary, warning, disabled, href, iconName, onClick, big, children, loading = false, className, style, submit = false, dataAttributes }) => {
    const emptyChildren = children === "" || children === null || children === undefined;
    const clName = cx("btn", { big }, { primary }, { warning }, className);
    const withIcon = iconName ? (React.createElement(Icon, { name: iconName, className: cx({ notext: emptyChildren }) })) : null;
    const buttonType = submit ? "submit" : "button";
    if (loading) {
        return React.createElement("button", { type: buttonType, className: cx("btn", { loading }, className) });
    }
    return href ? (React.createElement("a", { className: clName, href: href },
        withIcon,
        children)) : (React.createElement("button", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), type: buttonType, className: clName, style: style, disabled: disabled, onClick: onClick },
        withIcon,
        !emptyChildren ? children : null));
};

const SettingsItem = ({ text, children, className }) => {
    return (React.createElement("div", { className: cx("item", className) }, text ? (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "left" }, text),
        React.createElement("div", { className: "right" }, children))) : (React.createElement("div", null, children))));
};

const Settings = ({ user, title, settingsContent, onLogout, settingsVisible, toggleSettings, theme, toggleTheme, iconName, settingsTitle }) => {
    return (React.createElement("div", { className: cx("settings", { hidden: !settingsVisible }) },
        React.createElement("div", { className: "syspanel-top" },
            React.createElement("span", { className: "syspanel-title" }, title),
            React.createElement(Icon, { onClick: toggleSettings, className: "syspanel-close", name: "cross" })),
        React.createElement(Row, null,
            React.createElement(Column, { sm: 12, asContainer: true },
                user && (React.createElement("div", { className: "profile" },
                    user.profilePicture ? (React.createElement("img", { className: "profile-picture", src: user.profilePicture, title: "Profile picture" })) : (React.createElement(Icon, { name: iconName || "profile" })),
                    React.createElement("div", { className: "username" }, `${user.firstName || ""} ${user.lastName || ""}`),
                    user.header && React.createElement("div", { className: "header syspanel-header" }, user.header),
                    user.subHeader && React.createElement("div", { className: "syspanel-subheader" }, user.subHeader),
                    ((user.header !== undefined && user.header !== "") ||
                        (user.subHeader !== undefined && user.subHeader !== "")) && (React.createElement("div", { className: "dashedline" })))),
                React.createElement("div", { className: "content" },
                    React.createElement("div", { className: "title" }, settingsTitle || "My settings"),
                    toggleTheme && (React.createElement(SettingsItem, { text: "Switch theme", key: uniqueId() },
                        React.createElement(Switch, { onLabel: "Light", offLabel: "Dark", value: theme === "light", onChange: () => {
                                toggleTheme();
                            } }))),
                    settingsContent),
                onLogout && (React.createElement("div", { className: "bottom" },
                    React.createElement(Button, { big: true, onClick: onLogout },
                        React.createElement(Icon, { name: "logout" }),
                        "Sign out")))))));
};

const A_SECOND = 1000;
const A_MINUTE = A_SECOND * 60;
const AN_HOUR = A_MINUTE * 60;
const A_DAY = AN_HOUR * 24;
const A_MONTH = A_DAY * 30;
const A_YEAR = A_MONTH * 12;
const NotificationLog = ({ notificationsVisible, hideNotifications, deleteNotification, notificationsLogTitle }) => {
    const { notifications } = useAppContext();
    const noNotifications = () => React.createElement("div", { className: "notification-log-empty" }, "No new notifications.");
    const notificationSpan = (notification) => {
        const span = Math.round(new Date().getTime() - new Date(notification.timestamp).getTime());
        if (span < A_MINUTE) {
            return `${Math.round(span / A_SECOND)} s`;
        }
        if (span < AN_HOUR) {
            return `${Math.round(span / A_MINUTE)} m`;
        }
        if (span < A_DAY) {
            return `${Math.round(span / AN_HOUR)} h`;
        }
        if (span < A_MONTH) {
            return `${Math.round(span / A_DAY)} d`;
        }
        if (span < A_YEAR) {
            return `${Math.round(span / A_MONTH)} mo`;
        }
        return `${Math.round(span / A_YEAR)} y`;
    };
    const renderNotifications = () => {
        return notifications.items
            .filter((n) => !n.instant)
            .map((n) => {
            return (React.createElement("div", { key: uniqueId(), role: "button", tabIndex: 0, className: cx("notification-log-item", { new: n.new }), "data-timestamp": n.timestamp, onClick: n.onClick ? n.onClick : () => deleteNotification(n.id), onKeyDown: n.onClick ? n.onClick : () => deleteNotification(n.id), ...(n.dataAttributes && createDataAttributeObject(n.dataAttributes)) },
                React.createElement("div", { className: "title" }, n.title),
                React.createElement("div", { className: "description" }, n.description),
                React.createElement("span", { className: "notification-log-item-time" }, notificationSpan(n))));
        });
    };
    return (React.createElement("div", { className: cx("notification-log", { hidden: !notificationsVisible }) },
        React.createElement("div", { className: "syspanel-top" },
            React.createElement("span", { className: "syspanel-title" }, notificationsLogTitle || "Notifications"),
            React.createElement(Icon, { onClick: hideNotifications, className: "syspanel-close", name: "cross" })),
        React.createElement("div", { className: "notification-log-container" }, notifications.items && notifications.items.length
            ? renderNotifications()
            : noNotifications())));
};

const SysPanel = ({ title, user, settingsContent, onLogout, iconName, settingsTitle, notificationsLogTitle }) => {
    const { settings, theme, notifications } = useAppContext();
    const hideNotifications = () => {
        notifications.toggle();
        notifications.clearNew();
    };
    const containerRef = useClickOutside((e) => {
        const sysbar = document.querySelector(".sysbar") ? document.querySelector(".sysbar") : null;
        if (!(sysbar && sysbar.contains(e.target))) {
            notifications.setVisible(false);
            settings.setVisible(false);
        }
    });
    return (React.createElement("aside", { ref: containerRef, className: cx("syspanel", {
            hidden: !settings.visible && !notifications.visible
        }) },
        React.createElement(Settings, { title: title, user: user, settingsContent: settingsContent, onLogout: onLogout, settingsVisible: settings.visible, toggleSettings: settings.toggle, theme: theme.current, toggleTheme: theme.forced ? undefined : theme.toggle, iconName: iconName, settingsTitle: settingsTitle }),
        React.createElement(NotificationLog, { notificationsVisible: notifications.visible, hideNotifications: hideNotifications, deleteNotification: notifications.delete, notificationsLogTitle: notificationsLogTitle })));
};

const ActiveNotifications = () => {
    const { notifications } = useAppContext();
    const activeNotifications = notifications.items.filter(n => n.active).reverse();
    if (activeNotifications.length === 0) {
        return null;
    }
    const handleNotificationClicked = (notification) => {
        notifications.clear(notification.id);
        notification.onClick && notification.onClick();
    };
    const notificationClass = (notification) => {
        let className = "notification live";
        switch (notification.type) {
            case "primary":
                className += " stripe-blue";
                break;
            case "warning":
                className += " stripe-red";
                break;
            default:
                className += " stripe-" + notification.stripeColor;
                break;
        }
        return className;
    };
    return (React.createElement(React.Fragment, null, activeNotifications.map((notification, i) => (React.createElement("div", { key: `not_${i}`, "data-index": i, className: notificationClass(notification), onClick: () => handleNotificationClicked(notification), ...(notification.dataAttributes &&
            createDataAttributeObject(notification.dataAttributes)) },
        React.createElement("div", { className: "top-row" },
            React.createElement("div", { className: "title" },
                notification.icon && React.createElement(Icon, { name: notification.icon }),
                notification.icon && " ",
                notification.title),
            React.createElement(Icon, { onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    notifications.clear(notification.id);
                }, className: "close", name: "cross" })),
        notification.description !== "" && (React.createElement("div", { className: "description" }, notification.description)))))));
};

const BannerNotifications = () => {
    const { bannerNotifications } = useAppContext();
    const banners = bannerNotifications.items;
    if (banners.length === 0) {
        return null;
    }
    const handleNotificationClicked = (bannerNotification) => {
        bannerNotifications.delete(bannerNotification.id);
        bannerNotification.onClick && bannerNotification.onClick();
    };
    return (React.createElement(React.Fragment, null, banners.map((bannerNotification, i) => (React.createElement("div", { key: `not_${i}`, "data-index": i, className: "notification banner stripe-" + bannerNotification.stripeColor, onClick: () => handleNotificationClicked(bannerNotification), ...(bannerNotification.dataAttributes &&
            createDataAttributeObject(bannerNotification.dataAttributes)) },
        React.createElement("div", { className: "top-row" },
            React.createElement("div", { className: "title" },
                bannerNotification.icon && React.createElement(Icon, { name: bannerNotification.icon }),
                bannerNotification.icon && " ",
                bannerNotification.title),
            React.createElement(Icon, { onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    bannerNotifications.delete(bannerNotification.id);
                }, className: "close", name: "cross" })),
        bannerNotification.description !== "" && (React.createElement("div", { className: "description" }, bannerNotification.description)))))));
};

const GlobalNavigation = ({ globalNavigationData, dataAttributes }) => {
    const [globalNavHidden, setGlobalNavHidden] = useState(true);
    const [overlayHidden, setOverlayHidden] = useState(false);
    const { globalNavigation } = useAppContext();
    useEffect(() => {
        if (globalNavigation.visible) {
            setGlobalNavHidden(!globalNavigation.visible);
            setTimeout(() => {
                setOverlayHidden(!globalNavigation.visible);
            }, 200);
        }
        else {
            setOverlayHidden(!globalNavigation.visible);
            setTimeout(() => {
                setGlobalNavHidden(!globalNavigation.visible);
            }, 500);
        }
    }, [globalNavigation.visible]);
    return (React.createElement("aside", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("globalnav", { hidden: globalNavHidden }) },
        React.createElement("div", { className: cx("overlay", { hidden: overlayHidden }), onClick: globalNavigation.toggle }),
        React.createElement("div", { className: "tile sm-12" },
            React.createElement("div", { className: "header" },
                React.createElement("div", { className: "left" },
                    React.createElement("div", { className: "title" }, globalNavigationData.title ? globalNavigationData.title : "Global Navigation")),
                React.createElement("div", { className: "right" },
                    React.createElement("div", { className: "action close-global-nav" },
                        React.createElement("i", { className: "icon icon-cross", onClick: globalNavigation.toggle })))),
            React.createElement("div", { className: "content" },
                React.createElement("div", { className: "items" }, globalNavigationData?.quickLinks?.map((link, index) => {
                    return (React.createElement("div", { className: "link", key: index },
                        React.createElement("a", { href: link.href, onClick: () => link.onClick && link.onClick() }, link.title)));
                })),
                React.createElement("div", { className: "items" },
                    globalNavigationData.productLinks && React.createElement("div", { className: "title" }, "Products"),
                    globalNavigationData?.productLinks?.map((product, index) => {
                        const defaultIcon = product.internalIcon ? "icon-econ" : "icon-launch";
                        const icon = product.icon ? "icon-" + product.icon : defaultIcon;
                        return (React.createElement("div", { className: cx("item", { highlight: product.highlight }), onClick: () => product.onClick && product.onClick(), key: index },
                            React.createElement("div", { className: "product-symbol" },
                                React.createElement("i", { className: `icon ${icon}` })),
                            React.createElement("div", { className: "label" },
                                React.createElement("span", { className: "description" }, product.description),
                                React.createElement("span", { className: "acronym" }, product.acronym))));
                    }))))));
};

const SystemLayer = ({ className, applicationName, acronym, appId, onBrandClick, user, theme, forceTheme, persistTheme, onLogout, children, settingsContent, appBarItems, iconName, globalNavigationData, menuDefaultClosed = false, parentRef, hideNotificationBell = false, settingsTitle, notificationsLogTitle, persistentBanner, style, dataAttributes }) => {
    return (React.createElement(ApplicationContextProvider, { user: user, appId: appId || acronym, theme: theme, forcedTheme: forceTheme, skipPersistTheme: persistTheme === false, menuDefaultClosed: menuDefaultClosed },
        React.createElement("div", { className: className, ref: parentRef },
            React.createElement(SysBar, { applicationName: applicationName, acronym: acronym, onBrandClick: onBrandClick, appBarItems: appBarItems, iconName: iconName, globalNavigationIcon: globalNavigationData ? globalNavigationData.icon : undefined, hideNotificationBell: hideNotificationBell, hideGlobalNavigation: !globalNavigationData }),
            persistentBanner,
            React.createElement(BannerNotifications, null),
            React.createElement("main", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), style: style, className: className },
                React.createElement(SysPanel, { user: user, settingsContent: settingsContent, onLogout: onLogout, iconName: iconName, settingsTitle: settingsTitle, notificationsLogTitle: notificationsLogTitle }),
                globalNavigationData && React.createElement(GlobalNavigation, { globalNavigationData: globalNavigationData }),
                children ?? React.createElement("div", { className: "empty-app" })),
            React.createElement("div", { className: "notifications-wrapper" },
                React.createElement(ActiveNotifications, null)))));
};

const AppBar = ({ title, subtitle, actions, withMenu }) => {
    const noMenuStyle = {
        left: "1em"
    };
    const { menu } = useAppContext();
    return (React.createElement("nav", { className: cx("appbar") },
        React.createElement("div", { className: "actions-left" },
            withMenu && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "item", onClick: () => {
                        menu.toggle();
                        toggleNavigation();
                    }, onKeyDown: () => menu.toggle(), role: "button" },
                    React.createElement("i", { className: cx("navigation-toggle", { closed: menu.visible }) })),
                React.createElement("div", { className: cx("menu-anchor", { "open-menu": menu.visible }), onClick: () => {
                        menu.toggle();
                        toggleNavigation();
                    }, onKeyDown: () => menu.toggle(), role: "button" }, "Menu"))),
            React.createElement("div", { style: !withMenu ? noMenuStyle : undefined, className: cx("title", { "open-menu": withMenu && menu.visible }) },
                title,
                subtitle && React.createElement("span", { className: "subtitle" }, subtitle))),
        React.createElement("div", { className: "actions-right" }, actions)));
};

const AppMenu = ({ children }) => {
    const { menu } = useAppContext();
    return React.createElement("div", { className: cx("appnav", { hidden: !menu.visible }) }, menu.visible && children);
};

const ApplicationLayer = ({ className, title, subtitle, actions, menu, children, parentRef, style, dataAttributes }) => {
    const { settings, notifications } = useAppContext();
    return (React.createElement("div", { className: cx("app", className, {
            "slide-right": !settings.visible && !notifications.visible,
            "slide-left": settings.visible || notifications.visible
        }), ref: parentRef, ...(dataAttributes && createDataAttributeObject(dataAttributes)), style: style },
        React.createElement(AppBar, { title: title, subtitle: subtitle, actions: actions, withMenu: !!menu }),
        React.createElement("div", { className: "appbody" },
            menu && React.createElement(AppMenu, null, menu),
            React.createElement("div", { className: "appcontent" }, children))));
};

const SettingsNode = ({ title, children, className }) => {
    // <div className={cx("title", className)}>
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: cx("title", className) }, title),
        children));
};

const printCopyrightText = () => (React.createElement(React.Fragment, null,
    "\u00A9 Ericsson AB 2013-2023 - All Rights Reserved. ",
    React.createElement("br", null),
    "No part of this software may be reproduced in any form without the written permission of the copyright holder."));

const SignIn = (props) => {
    const { anonymousSignIn, signInButtons, productName, onSignIn, signedInUser, invalidCredentials, support, style, className, dataAttributes, disclaimer } = {
        ...props
    };
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [userNameFocused, setUserNameFocus] = useState(false);
    const [passwordFocused, setPasswordFocus] = useState(false);
    useEffect(() => {
        if (!document.body.classList.contains("dark")) {
            document.body.classList.remove("light", "dark");
            document.body.classList.add("dark");
        }
    }, []);
    const buttons = signInButtons && signInButtons.length
        ? signInButtons
        : [{ text: "Sign in", onClick: onSignIn }];
    const unfocus = () => {
        setUserNameFocus(false);
        setPasswordFocus(false);
    };
    return (React.createElement("main", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), style: style, className: className },
        React.createElement("div", { className: "signin" },
            React.createElement("div", { className: "watermark" },
                React.createElement("i", { className: "icon icon-econ" }),
                React.createElement("div", { className: "product" }, productName)),
            signedInUser && (React.createElement("div", { className: "welcome" },
                React.createElement("div", { className: "message" }, "Welcome"),
                React.createElement("div", { className: "username" }, `${signedInUser.firstName} ${signedInUser.lastName}`),
                React.createElement("div", { className: "loading" }))),
            React.createElement("div", { className: cx("error", { hidden: !invalidCredentials }) }, "Your email or password is incorrect"),
            !signedInUser && (React.createElement("form", { action: "/templates/signin", method: "get", className: "" },
                !anonymousSignIn && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "field validate" },
                        React.createElement("label", { htmlFor: "username" }, "Username"),
                        React.createElement("input", { type: "email", name: "username", id: "username", placeholder: "", tabIndex: 0, value: userName, required: true, onFocus: () => {
                                setUserNameFocus(true);
                                setPasswordFocus(false);
                            }, onBlur: () => {
                                unfocus();
                            }, className: cx({
                                "field-filled": userName.length > 0 || userNameFocused
                            }), onChange: (e) => setUserName(e.target.value) }),
                        React.createElement("div", { className: "hint" })),
                    React.createElement("div", { className: "field validate" },
                        React.createElement("label", { htmlFor: "password" }, "Password"),
                        React.createElement("input", { type: "password", name: "password", id: "password", placeholder: " ", tabIndex: 0, required: true, onFocus: () => {
                                setPasswordFocus(true);
                                setUserNameFocus(false);
                            }, onBlur: () => {
                                unfocus();
                            }, className: cx({
                                "field-filled": password.length > 0 || passwordFocused
                            }), value: password, onChange: (e) => setPassword(e.target.value) }),
                        React.createElement("div", { className: "hint" })))),
                React.createElement("div", { className: "signin-buttons" }, buttons.map(b => (React.createElement("button", { key: b.text, type: "button", className: "btn big primary", onClick: () => b.onClick && b.onClick(userName, password) }, b.text)))),
                React.createElement("div", { className: "help" }, support))),
            React.createElement("div", { className: "footer" },
                printCopyrightText(),
                React.createElement("br", null),
                disclaimer))));
};

const Tooltip = ({ type = "dotted", position = "top", primary, text, content, alwaysShown, children, color = "default", className, style, dataAttributes, as }) => {
    const Wrapper = ({ children }) => {
        if (as === "div") {
            return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("tooltip", { [type]: type }, className), style: style }, children));
        }
        else {
            return (React.createElement("span", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("tooltip", { [type]: type }, className), style: style }, children));
        }
    };
    return (React.createElement(Wrapper, null,
        children,
        React.createElement("span", { className: cx("message", { [position]: position }, { blue: primary }, { [color]: color }, { visible: alwaysShown }) }, text
            ? text.split("\\n").map((l, i) => {
                return (React.createElement("div", { key: l + i },
                    l,
                    React.createElement("br", null)));
            })
            : content)));
};

const AppBarItem = ({ iconName, text, tooltip, onClick, className, style, dataAttributes }) => {
    const content = (React.createElement(React.Fragment, null,
        iconName && React.createElement("i", { className: cx(`icon icon-${iconName}`, className) }),
        text && React.createElement("span", null, text)));
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), role: "presentation", className: cx("item", { defaultCursor: !onClick }), style: style, onClick: e => {
            if (onClick)
                onClick(e);
        } }, tooltip ? (React.createElement(Tooltip, { text: tooltip, position: "bottom", type: "pointer" }, content)) : (content)));
};

const AppBarAnchor = ({ iconName, href, openInNewTab, tooltip, className, style, dataAttributes }) => {
    const extraProps = openInNewTab ? { target: "_blank", rel: "noreferrer" } : {};
    const content = iconName && (React.createElement("a", { href: href, ...extraProps },
        React.createElement("i", { className: cx(`icon icon-${iconName}`, className) })));
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), style: style, role: "presentation", className: "item hover" }, tooltip ? (React.createElement(Tooltip, { text: tooltip, position: "bottom", type: "pointer" }, content)) : (content)));
};

const Loader = ({ size, className, style, dataAttributes }) => (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("loading", size || "small", className), style: style }));

const Welcome = (props) => {
    const { productName, signedInUser, welcomeText = "Welcome", style, className, dataAttributes } = {
        ...props
    };
    useEffect(() => {
        if (!document.body.classList.contains("dark")) {
            document.body.classList.remove("light", "dark");
            document.body.classList.add("dark");
        }
    }, []);
    return (React.createElement("main", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), style: style, className: className },
        React.createElement("div", { className: "signin" },
            React.createElement("div", { className: "watermark" },
                React.createElement("i", { className: "icon icon-econ" }),
                React.createElement("div", { className: "product" }, productName)),
            React.createElement("div", { className: "welcome" },
                React.createElement("div", { className: "message" }, welcomeText),
                signedInUser && (React.createElement("div", { className: "username" }, `${signedInUser.firstName} ${signedInUser.lastName}`)),
                React.createElement(Loader, { size: "medium" })),
            React.createElement("div", { className: "footer" }, printCopyrightText()))));
};

const Splash = (props) => {
    const { productName, content, children, footer, style, className, dataAttributes } = {
        ...props
    };
    useEffect(() => {
        if (!document.body.classList.contains("dark")) {
            document.body.classList.remove("light", "dark");
            document.body.classList.add("dark");
        }
    }, []);
    return (React.createElement("main", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), style: style, className: className },
        React.createElement("div", { className: "signin" },
            React.createElement("div", { className: "watermark" },
                React.createElement("i", { className: "icon icon-econ" }),
                React.createElement("div", { className: "product" }, productName)),
            React.createElement("div", { className: "welcome" }, children || content),
            React.createElement("div", { className: "footer" }, footer || printCopyrightText()))));
};

const PersistentBanner = ({ icon, title, description, stripeColor, onClick }) => {
    const persistentBannerClass = () => {
        let className = "notification banner stripe-" + stripeColor;
        if (onClick === undefined) {
            className += " no-hover";
        }
        return className;
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: persistentBannerClass(), onClick: onClick },
            React.createElement("div", { className: "top-row" },
                React.createElement("div", { className: "title" },
                    icon && React.createElement(Icon, { name: icon }),
                    icon && " ",
                    title)),
            description !== "" && React.createElement("div", { className: "description" }, description))));
};

const useApplication = () => {
    const { menu, globalNavigation } = useAppContext();
    return {
        menuVisible: menu.visible,
        toggleMenu: menu.toggle,
        globalNavigationVisible: globalNavigation.visible,
        globalNavigation: globalNavigation
    };
};

const MenuContext = createContext({
    closeOnSelect: false,
    toggle: () => { }
});
const MenuContextProvider = ({ value, children }) => {
    const { toggleMenu } = useApplication();
    return (React.createElement(MenuContext.Provider, { value: { ...value, toggle: toggleMenu } }, children));
};
const useMenu = () => {
    return useContext(MenuContext);
};

const Menu = ({ children, className, style, closeOnSelect, dataAttributes }) => {
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("tree navigation", className), style: style },
        React.createElement("ul", null,
            React.createElement(MenuContextProvider, { value: { closeOnSelect } }, children))));
};

const MenuNode = ({ title, children, className, style, initialExpandedState = false, expanded, dataAttributes, onExpand }) => {
    const [_expanded, setExpanded] = useState(initialExpandedState);
    const handleExpand = () => {
        setExpanded(!_expanded);
        if (onExpand) {
            onExpand(!_expanded);
        }
    };
    return (React.createElement("li", { ...(dataAttributes && createDataAttributeObject(dataAttributes)) },
        React.createElement("span", { role: "presentation", style: style, className: cx("item title", { opened: expanded !== undefined ? expanded : _expanded }, className), onKeyPress: handleExpand, onClick: handleExpand }, title),
        React.createElement("ul", null, children)));
};

const MenuItem = ({ tag, children, className, style, dataAttributes, ...rest }) => {
    const Tag = tag;
    const { closeOnSelect, toggle } = useMenu();
    return (React.createElement("li", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), role: "presentation", onClick: () => {
            if (closeOnSelect) {
                toggle();
            }
        } },
        React.createElement(Tag, { className: cx("item", className), style: style, ...rest }, children)));
};

const Accordion = ({ children, disabled, lines, className, dataAttributes, style }) => {
    return (React.createElement("div", { style: style, ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("accordion", { disabled }, { lines }, className) },
        React.createElement("ul", { className: "accordion-list" }, children)));
};

const AccordionItem = ({ title, children, disabled, className, style, noInset = false, onOpen, onClose, opened, dataAttributes }) => {
    const toggleOpened = (opened) => {
        if (opened && onOpen) {
            onOpen();
        }
        else if (!opened && onClose) {
            onClose();
        }
    };
    return (React.createElement("li", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), role: "presentation", className: cx("accordion-item", { disabled }, { opened }, className), onClick: () => toggleOpened(!opened), style: style },
        React.createElement("div", { role: "presentation", className: "title" },
            React.createElement("div", { style: { minHeight: "17px" } }, title)),
        React.createElement("div", { role: "presentation", className: cx("content", { "no-inset": noInset }), onClick: (event) => event.stopPropagation() }, children)));
};

const Tree = ({ children, className, style, disabled, dataAttributes }) => {
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("tree", { disabled }, className), style: style },
        React.createElement("ul", null, children)));
};

const TreeNode = ({ title, children, icon, customComponent, onClick, onExpand, className, style, defaultExpanded = false, disabled, expanded, dataAttributes }) => {
    const [_expanded, setExpanded] = useState(defaultExpanded);
    const defaultItem = (React.createElement(React.Fragment, null,
        icon && typeof icon === "string" ? React.createElement(Icon, { name: icon, small: true, base: true }) : icon,
        title));
    const item = customComponent !== undefined ? customComponent : defaultItem;
    const handleExpand = () => {
        setExpanded(!_expanded);
        if (onClick !== undefined) {
            onClick(!_expanded);
        }
        if (onExpand) {
            onExpand(!_expanded);
        }
    };
    return (React.createElement("li", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), style: style },
        React.createElement("span", { role: "presentation", className: cx("item title", { opened: expanded || _expanded, disabled }, className), onKeyPress: () => setExpanded(!_expanded), onClick: handleExpand }, item),
        React.createElement("ul", null, children)));
};

const TreeItem = ({ tag, children, className, style, disabled, dataAttributes, ...rest }) => {
    const Tag = tag;
    return (React.createElement("li", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), style: style },
        React.createElement(Tag, { className: cx("item", { disabled }, className), style: style, ...rest }, children)));
};

const TileActionButton = ({ iconName, tooltipText, disabled, active, onClick, className, style, dataAttributes }) => {
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("action", className), style: style }, tooltipText ? (React.createElement(Tooltip, { position: "left", text: tooltipText, type: "pointer" },
        React.createElement(Icon, { name: iconName, disabled: disabled, active: active, onClick: onClick }))) : (React.createElement(Icon, { name: iconName, disabled: disabled, active: active, onClick: onClick }))));
};

const Tile = ({ type = "tile", noMargin, title, subtitle, children, bottom, actions, leftActions, contentClassName, bottomClassName, canMaximize, onMaximize, sm, md, lg, xl, className, style, contentRef, dropdownMenu, parentRef, defaultMaximized = false, dataAttributes }) => {
    const [isFullscreen, setFullScreen] = useState(defaultMaximized);
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), ref: parentRef, style: style, className: cx(type, {
            fullscreen: isFullscreen,
            [`sm-${sm}`]: sm,
            [`md-${md}`]: md,
            [`lg-${lg}`]: lg,
            [`xl-${xl}`]: xl
        }, className) },
        (title || subtitle || leftActions || actions || canMaximize || dropdownMenu) && (React.createElement("div", { className: "header" },
            React.createElement("div", { className: "left" },
                leftActions && (React.createElement(React.Fragment, null,
                    leftActions,
                    React.createElement("span", { className: "separator" }))),
                title && React.createElement("span", { className: "title" }, title),
                subtitle && React.createElement("span", { className: "subtitle" }, subtitle)),
            React.createElement("div", { className: "right" },
                actions,
                canMaximize && (React.createElement(TileActionButton, { key: "act_maximize", tooltipText: isFullscreen ? "Minimize" : "Maximize", onClick: () => {
                        if (onMaximize) {
                            onMaximize(!isFullscreen);
                        }
                        setFullScreen(!isFullscreen);
                    }, iconName: isFullscreen ? "minimize" : "maximize" })),
                dropdownMenu && dropdownMenu))),
        children && (React.createElement("div", { ref: contentRef, className: cx("content", { "no-margins": noMargin }, contentClassName) }, children)),
        bottom && React.createElement("div", { className: cx("bottom", bottomClassName) }, bottom)));
};

const MultiPanelTile = ({ centerTitle, centerSubtitle, centerLeftIcon, centerRightActions, centerContent, leftTitle, leftSubtitle, leftVisible, leftContent, leftActions, rightTitle, rightSubtitle, rightVisible, rightContent, rightActions, sm, md, lg, xl, className, style, leftClassName, middleClassName, rightClassName, parentRef, dataAttributes }) => {
    const [leftInitialized, setLeftInitialized] = useState(false);
    const [rightInitialized, setRightInitialized] = useState(false);
    const [LeftVisible, setLeftVisible] = useState(leftVisible);
    const [RightVisible, setRightVisible] = useState(rightVisible);
    useEffect(() => {
        setLeftVisible(leftVisible);
        setLeftInitialized(true);
        toggleTiles();
    }, [leftVisible]);
    useEffect(() => {
        setRightVisible(rightVisible);
        setRightInitialized(true);
        toggleTiles();
    }, [rightVisible]);
    // Windowresize listener
    const [, updateState] = React.useState();
    const forceUpdate = useCallback(() => updateState({}), []);
    useEffect(() => {
        const handleResize = () => {
            forceUpdate();
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [forceUpdate]);
    const willHideRightPanel = !rightInitialized && !rightVisible && !isMobileDevice();
    const rightPanelMobileStyling = isMobileDevice() ? {} : undefined;
    const rightPanelStyling = willHideRightPanel ? {} : rightPanelMobileStyling;
    const leftPanel = () => {
        return (React.createElement("div", { className: cx("left-panel ", {
                animateLeftPanel: leftInitialized,
                hidden: !leftInitialized ||
                    (leftInitialized && !LeftVisible) ||
                    (isTabletDevice() && !leftInitialized && !LeftVisible) ||
                    (isTabletDevice() && RightVisible) ||
                    (isMobileDevice() && !leftInitialized && !LeftVisible) ||
                    (isMobileDevice() && LeftVisible && RightVisible)
            }) },
            React.createElement("div", { className: "header" },
                React.createElement("div", { className: "left" },
                    React.createElement("span", { className: "title" }, leftTitle),
                    React.createElement("span", { className: "subtitle" }, leftSubtitle)),
                React.createElement("div", { className: "right" }, leftActions)),
            React.createElement("div", { className: cx("content", leftClassName) }, leftContent)));
    };
    const middlePanel = () => {
        return (React.createElement("div", { className: cx("mid-panel", {
                hidden: isMobileDevice() && (LeftVisible || RightVisible)
            }) },
            React.createElement("div", { className: "header" },
                React.createElement("div", { className: "left" },
                    centerLeftIcon && (React.createElement(React.Fragment, null,
                        centerLeftIcon,
                        React.createElement("span", { className: "separator" }))),
                    React.createElement("span", { className: "title" }, centerTitle || ""),
                    React.createElement("span", { className: "subtitle" }, centerSubtitle || "")),
                React.createElement("div", { className: "right" }, centerRightActions)),
            React.createElement("div", { className: cx("content", middleClassName) }, centerContent && !(isMobileDevice() && (LeftVisible || RightVisible)) && centerContent)));
    };
    const rightPanel = () => {
        return (React.createElement("div", { className: cx("right-panel", {
                hidden: !rightInitialized ||
                    (rightInitialized && !RightVisible) ||
                    (isMobileDevice() && !rightInitialized && !RightVisible)
            }), style: rightPanelStyling },
            React.createElement("div", { className: "header" },
                React.createElement("div", { className: "left" },
                    React.createElement("span", { className: "title" }, rightTitle),
                    React.createElement("span", { className: "subtitle" }, rightSubtitle)),
                React.createElement("div", { className: "right" }, rightActions)),
            React.createElement("div", { className: cx("content", rightClassName) }, rightContent)));
    };
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), ref: parentRef, className: cx("tile", {
            [`sm-${sm}`]: sm,
            [`md-${md}`]: md,
            [`lg-${lg}`]: lg,
            [`xl-${xl}`]: xl
        }, className), style: style },
        React.createElement("div", { className: "content" },
            React.createElement("div", { className: "row multi-panel-tile " },
                leftPanel(),
                middlePanel(),
                rightPanel()))));
};

const ProgressBar = ({ max = 0, value = 0, buffer, color, label, thin, className, disabled, format = "percent", indeterminate = false, style, dataAttributes }) => {
    const id = uniqueId();
    const progressBarLabel = (React.createElement("label", { className: "progress-bar-label", htmlFor: id }, label));
    const percent = Math.round((value / max) * 100);
    let progressbarValue = "";
    if (format === "percent") {
        progressbarValue = `${percent}%`;
    }
    if (format === "value") {
        progressbarValue = `${value}/${max}`;
    }
    const progressbarValueWidth = (progressbarValue.toString().length * 12) / 1.5;
    if (indeterminate) {
        return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("progress-bar", { disabled }, color, thin ? "thin" : "", className), style: style },
            buffer && label ? (React.createElement("div", { style: { marginBottom: "-17px" } }, progressBarLabel)) : (label && progressBarLabel),
            buffer && React.createElement("progress", { className: "buffer", value: buffer, max: max }),
            React.createElement("progress", { className: cx("progress-bar-progress", {
                    shorten: format === "value"
                }), id: id, max: max, style: format === "value" ? { width: `calc(100% - ${15}px)` } : {} })));
    }
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("progress-bar", { disabled }, color, thin ? "thin" : "", className), style: style },
        buffer && label ? React.createElement("div", null, progressBarLabel) : label && progressBarLabel,
        buffer && React.createElement("progress", { className: "buffer", value: buffer, max: max }),
        React.createElement("progress", { className: cx("progress-bar-progress", { shorten: format === "value" }), id: id, value: value, max: max, style: format === "value" ? { width: `calc(100% - ${15 + progressbarValueWidth}px)` } : {} }),
        React.createElement("span", { className: "progress-bar-value" }, progressbarValue)));
};

const NumberField = ({ label, value, onChange, onBlur, onKeyDown, onKeyPress, onKeyUp, focus, disabled, readonly, max = Number.MAX_SAFE_INTEGER, min = 0, step = 1, type, className, style, dataAttributes }) => {
    const { inputRef } = useFocus();
    const onChangeHandler = (e) => {
        const newValue = parseFloat(e.target.value);
        if (onChange && ((newValue >= min && newValue <= max) || !e.target.value)) {
            onChange(newValue);
        }
    };
    const increase = () => {
        if (onChange && value + step <= max) {
            onChange((value + step) * 1);
        }
    };
    const decrease = () => {
        if (onChange && value - step >= min) {
            onChange((value - step) * 1);
        }
    };
    const id = uniqueId();
    return (React.createElement("span", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx({ spinner: type === "spinner", disabled }, className) },
        label && React.createElement("label", { htmlFor: id }, label),
        React.createElement("input", { style: style, ref: focus ? inputRef : null, id: id, type: "number", value: isNaN(value) ? "" : value, onChange: onChangeHandler, onBlur: (e) => onBlur && onBlur(e), onKeyDown: (e) => onKeyDown && onKeyDown(e), onKeyUp: (e) => onKeyUp && onKeyUp(e), onKeyPress: (e) => onKeyPress && onKeyPress(e), disabled: disabled, max: max, min: min, step: step, readOnly: readonly }),
        type === "spinner" && (React.createElement("div", { className: "controls" },
            React.createElement("i", { role: "presentation", className: cx("icon icon-chevron-down", { disabled: value <= min }), onClick: !readonly ? decrease : undefined }),
            React.createElement("i", { role: "presentation", className: cx("icon icon-chevron-up", { disabled: value >= max }), onClick: !readonly ? increase : undefined })))));
};

const SingleSelect = ({ type, items, disabled, placeholder, label = "", topLabel, onChange, onOpen, onClose, onBlur, onChangeFilter, className, dataAttributes }) => {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState(label);
    const [activeItemIndex, setActiveItemIndex] = useState(-1);
    const labelRef = useRef(null);
    const [scrollIndex, setScrollIndex] = useState(0);
    const itemRefs = useRef([]);
    const [notSelected, setNotSelected] = useState(label);
    const [filteredItems, setFilteredItems] = useState([]);
    const [hoverIndex, setHoverIndex] = useState(-1);
    const toggleOpen = () => {
        setOpen(!open);
    };
    useEffect(() => {
        setText(label);
    }, [label]);
    const filterOptions = useCallback((typedValue) => {
        if (items !== undefined) {
            setFilteredItems(items.filter(val => val.title.toLowerCase().indexOf(typedValue.toLowerCase()) > -1));
        }
    }, [items]);
    const handleChangeFilter = useCallback((val) => {
        onChangeFilter && onChangeFilter(val);
    }, [onChangeFilter]);
    useEffect(() => {
        if (type === "input-single") {
            filterOptions(text);
        }
        else {
            setFilteredItems(items);
        }
    }, [filterOptions, items, text, type]);
    useEffect(() => {
        if (scrollIndex >= 0 && itemRefs.current[scrollIndex]) {
            itemRefs.current[scrollIndex].scrollIntoView({
                behavior: "smooth",
                block: "nearest"
            });
        }
    }, [scrollIndex]);
    const validTypes = ["single", "input-single"];
    const selectedDataType = type !== undefined ? type : "single";
    const closeSelect = () => {
        if (onClose) {
            onClose();
        }
        setOpen(false);
    };
    const containerRef = useClickOutside(() => {
        setText(label);
        closeSelect();
    }, open);
    const moveActiveItem = (direction) => {
        const newIndex = direction === "down"
            ? (activeItemIndex + 1) % filteredItems.length
            : activeItemIndex === 0
                ? filteredItems.length - 1
                : (activeItemIndex - 1) % filteredItems.length;
        setActiveItemIndex(newIndex);
        setScrollIndex(newIndex);
        setHoverIndex(newIndex);
        if (selectedDataType === "input-single") {
            const item = filteredItems[newIndex];
            if (!item || item.disabled)
                return;
            setNotSelected(item.title);
        }
        if (selectedDataType !== "input-single") {
            const item = filteredItems[newIndex];
            if (!item || item.disabled)
                return;
            setText(item.title);
            if (onChange) {
                onChange(item);
            }
        }
    };
    const handleEnter = () => {
        if (selectedDataType === "input-single") {
            setText(notSelected);
            setNotSelected("");
            setActiveItemIndex(-1);
        }
        if (onClose) {
            onClose();
        }
    };
    const handleKeyDown = (e) => {
        switch (e.key) {
            case "ArrowDown":
                moveActiveItem("down");
                break;
            case "ArrowUp":
                moveActiveItem("up");
                break;
            case "Enter":
                handleEnter();
                break;
        }
    };
    const invalidType = (React.createElement("div", { className: "select closed", "data-type": "invalid-type" },
        React.createElement("div", { className: "invalid_select" }, "Invalid type")));
    const renderItem = (item, index) => (React.createElement("div", { ref: el => {
            itemRefs.current[index] = el;
        }, key: item.title + "-" + index, title: item.title, role: "presentation", onClick: e => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            if (!item.disabled) {
                setText(item.title);
                setOpen(false);
                if (onChange !== undefined) {
                    onChange(item);
                }
                if (onClose) {
                    onClose();
                }
            }
        }, className: cx("item", { active: item.title === text }, { disabled: item.disabled }, { highlight: index === hoverIndex }) }, item.title));
    const buttonType = selectedDataType === "input-single" ? (React.createElement("div", { role: "presentation", className: "clickable current-options", onClick: () => {
            setOpen(true);
            if (onOpen) {
                onOpen();
            }
        } },
        React.createElement("input", { value: text || "", type: "text", placeholder: placeholder, onFocus: () => {
                setOpen(true);
            }, onBlur: (e) => {
                onBlur && onBlur(e);
            }, onChange: (e) => {
                if (e.target.value === "") {
                    setOpen(true);
                }
                setText(e.target.value);
                filterOptions(e.target.value);
                handleChangeFilter(e.target.value);
            }, onKeyDown: handleKeyDown }),
        React.createElement(Icon, { onClick: e => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                toggleOpen();
                if (!open) {
                    setOpen(true);
                    if (onOpen) {
                        onOpen();
                    }
                }
                else {
                    setOpen(false);
                    if (onClose) {
                        onClose();
                    }
                }
            }, name: "chevron-down" }))) : (React.createElement("button", { type: "button", role: "presentation", onBlur: (e) => onBlur && onBlur(e), className: "btn current-options", onClick: e => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            if (!open && onOpen) {
                onOpen();
            }
            if (!!open && onClose) {
                onClose();
            }
            setOpen(!open);
        }, onKeyDown: handleKeyDown }, text || placeholder || ""));
    const isOutOfViewport = (elem) => {
        const itemHeightOffset = 32;
        const maxOffset = 130;
        const offsetHeight = items.length < 4 ? itemHeightOffset * items.length : maxOffset;
        const bounding = elem.getBoundingClientRect();
        return (bounding.bottom + offsetHeight > (window.innerHeight || document.documentElement.clientHeight));
    };
    const outOfViewport = containerRef.current ? isOutOfViewport(containerRef.current) : false;
    if (validTypes.includes(selectedDataType)) {
        return (React.createElement(React.Fragment, null,
            topLabel && React.createElement("label", { ref: labelRef }, topLabel),
            React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), title: text, ref: containerRef, role: "presentation", className: cx("select", { disabled }, { closed: !open }, { open }, className), "data-type": selectedDataType },
                buttonType,
                React.createElement("div", { className: cx("options-list overflow", { above: outOfViewport }) },
                    type === "input-single" && filteredItems.length === 0 && text !== "" ? (React.createElement("div", { className: "no_results" }, "No results found")) : null,
                    filteredItems.map((item, index) => {
                        return renderItem(item, index);
                    })))));
    }
    return invalidType;
};

const Pill = ({ icon, severity, disabled, onToggle, toggled, onRemove, onMouseMove, onMouseLeave, onClick, children, className, style, dataAttributes }) => {
    const withIcon = typeof icon === "string" ? React.createElement(Icon, { name: icon }) : icon;
    const severityClass = severity ? `severity-${severity}` : null;
    const clName = cx("pill", severityClass, { toggle: onToggle }, { unselected: toggled && onToggle }, { removable: onRemove }, { disabled }, className);
    const onClickHandler = (e) => {
        if (onRemove) {
            onRemove(e);
        }
        if (onToggle) {
            onToggle(!toggled);
        }
        if (onClick) {
            onClick();
        }
    };
    return (React.createElement("span", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), role: "presentation", className: clName, style: style, onClick: onClickHandler, onMouseMove: onMouseMove !== undefined ? onMouseMove : undefined, onMouseLeave: onMouseLeave !== undefined ? onMouseLeave : undefined },
        withIcon,
        children));
};

const MultiSelect = ({ type, items, disabled, placeholder, label, topLabel, selectedItems, onChange, onOpen, onClose, onBlur, className, style, dataAttributes, onChangeFilter }) => {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState(label || "");
    const labelRef = useRef(null);
    const [filteredItems, setFilteredItems] = useState([]);
    const toggleOpen = () => {
        setOpen(!open);
    };
    const optionscontainerRef = useRef(null);
    const filterOptions = useCallback((typedValue) => {
        if (items !== undefined) {
            setFilteredItems(items.filter(val => val.title.toLocaleLowerCase().indexOf(typedValue.toLocaleLowerCase()) > -1));
        }
    }, [items]);
    const handleChangeFilter = useCallback((val) => {
        onChangeFilter && onChangeFilter(val);
    }, [onChangeFilter]);
    useEffect(() => {
        if (type === "input-multi") {
            filterOptions(text);
        }
        else {
            setFilteredItems(items);
        }
    }, [filterOptions, items, text, type]);
    const validTypes = ["multi", "multi-pills", "input-multi"];
    const selectedDataType = type !== undefined ? type : "multi";
    const closeSelect = () => {
        if (open && onClose) {
            onClose();
        }
        setOpen(false);
    };
    const containerRef = useClickOutside(() => {
        setText(label || "");
        closeSelect();
    }, open);
    const invalidType = (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: "select closed", "data-type": "invalid-type" },
        React.createElement("div", { className: "invalid_select" }, "Invalid type")));
    const renderMultiItem = (item, index) => {
        const id = uniqueId();
        const selectedLabels = selectedItems !== undefined ? selectedItems.map(cur => cur.title) : [];
        const checked = selectedItems !== undefined && selectedLabels.includes(item.title);
        return (React.createElement("div", { style: style, className: cx("item", { disabled: item.disabled }), key: item.title + index, title: item.title },
            React.createElement("input", { type: "checkbox", id: id, checked: checked, onClick: e => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                }, onBlur: (e) => onBlur && onBlur(e), onChange: () => {
                    if (onChange !== undefined && !item.disabled) {
                        let returnValues;
                        if (checked) {
                            returnValues =
                                selectedItems !== undefined
                                    ? selectedItems.filter((l) => l.title !== item.title)
                                    : [];
                        }
                        else {
                            returnValues = selectedItems !== undefined ? [item, ...selectedItems] : [item];
                        }
                        onChange(returnValues);
                    }
                } }),
            React.createElement("label", { htmlFor: id, onClick: e => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                } }, item.title)));
    };
    const pills = () => {
        const pillsToAdd = [];
        const selectedLabels = selectedItems !== undefined ? selectedItems.map(cur => cur.title) : [];
        if ((selectedDataType === "multi-pills" || selectedDataType === "input-multi") &&
            selectedItems !== undefined) {
            for (let i = 0; i < selectedItems.length; i += 1) {
                pillsToAdd.push(React.createElement(Pill, { key: i, onRemove: () => onChange !== undefined
                        ? onChange(selectedItems.filter((l) => l.title !== selectedLabels[i]))
                        : null }, `${selectedItems[i].title}`));
            }
        }
        return (React.createElement("div", { ref: optionscontainerRef, className: "options_container" }, pillsToAdd));
    };
    const buttonType = selectedDataType === "input-multi" ? (React.createElement("div", { role: "presentation", className: "clickable current-options", onClick: () => {
            setOpen(true);
            if (onOpen) {
                onOpen();
            }
        } },
        React.createElement("input", { value: text || "", type: "text", placeholder: placeholder, onFocus: () => {
                setOpen(true);
            }, onBlur: (e) => {
                onBlur && onBlur(e);
            }, onChange: (e) => {
                if (e.target.value === "") {
                    setOpen(true);
                }
                setText(e.target.value);
                filterOptions(e.target.value);
                handleChangeFilter(e.target.value);
            } }),
        React.createElement(Icon, { onClick: e => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                toggleOpen();
                if (!open) {
                    setOpen(true);
                    if (onOpen) {
                        onOpen();
                    }
                }
                else {
                    setOpen(false);
                    if (onClose) {
                        onClose();
                    }
                }
            }, name: "chevron-down" }))) : (React.createElement("button", { type: "button", className: "btn current-options", role: "presentation", onBlur: (e) => onBlur && onBlur(e), onClick: e => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setOpen(!open);
            if (!open && onOpen) {
                onOpen();
            }
            if (!!open && onClose) {
                onClose();
            }
        } }, label || placeholder || ""));
    const isOutOfViewport = (elem) => {
        const itemHeightOffset = 32;
        const maxOffset = 130;
        const offsetHeight = items.length < 4 ? itemHeightOffset * items.length : maxOffset;
        const bounding = elem.getBoundingClientRect();
        return (bounding.bottom + offsetHeight > (window.innerHeight || document.documentElement.clientHeight));
    };
    const outOfViewport = containerRef.current ? isOutOfViewport(containerRef.current) : false;
    const optionscontainerHeight = optionscontainerRef.current
        ? optionscontainerRef.current.offsetHeight
        : 0;
    if (validTypes.includes(selectedDataType)) {
        return (React.createElement(React.Fragment, null,
            topLabel && React.createElement("label", { ref: labelRef }, topLabel),
            React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), title: label },
                React.createElement("div", { ref: containerRef, role: "presentation", className: cx("select", { disabled }, { closed: !open }, { open }, className), "data-type": selectedDataType },
                    buttonType,
                    React.createElement("div", { className: cx("options-list overflow", { above: outOfViewport }), style: outOfViewport &&
                            (selectedDataType === "multi-pills" || selectedDataType === "input-multi")
                            ? { bottom: `${28 + optionscontainerHeight}px` }
                            : undefined },
                        type === "input-multi" && filteredItems.length === 0 && text !== "" ? (React.createElement("div", { className: "no_results" }, "No results found")) : null,
                        filteredItems.map((item, index) => {
                            return renderMultiItem(item, index);
                        })),
                    pills()))));
    }
    return invalidType;
};

const Pagination = ({ pages, start, end, activePage = 1, onLeftClick, onRightClick, onPageClick, onUpClick, onDownClick, showPageSelector, showItemsPerPageSelector, itemsPerPageOptions, onItemsPerPageChanged, onPageChanged, currentItemsPerPage, vertical, reverse, className, style, disabled, dataAttributes }) => {
    let nPages = 0;
    if (pages !== undefined) {
        if (Number.isInteger(pages)) {
            nPages = pages;
        }
        else {
            throw new Error("The value of 'pages' must be an integer.");
        }
    }
    const firstPage = start !== undefined ? start : 1;
    const lastPage = end !== undefined ? end : nPages;
    /** Listeners */
    const handleUpClick = () => {
        if (activePage && activePage < lastPage && onUpClick && !reverse) {
            onUpClick(activePage + 1);
        }
        if (activePage && activePage > firstPage && onUpClick && reverse) {
            onUpClick(activePage - 1);
        }
    };
    const handleDownClick = () => {
        if (activePage && activePage > firstPage && onDownClick && !reverse) {
            onDownClick(activePage - 1);
        }
        if (activePage && activePage < lastPage && onDownClick && reverse) {
            onDownClick(activePage + 1);
        }
    };
    const handleLeftClick = () => {
        if (activePage && activePage > firstPage && onLeftClick) {
            onLeftClick(activePage - 1);
        }
    };
    const handleRightClick = () => {
        if (activePage && activePage < lastPage && onRightClick) {
            onRightClick(activePage + 1);
        }
    };
    const handlePageClick = (clickedPage) => {
        onPageClick(clickedPage);
    };
    /** Components */
    const ellipsis = (key) => React.createElement("li", { className: "ellipsis", key: `ellipsis-${key}` });
    const page = (index, active) => {
        return (React.createElement("li", { style: style, key: index, className: cx("pageItem", { active }), role: "presentation" },
            React.createElement("a", { role: "button", href: "#", className: "pageItemClickable", onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePageClick(index);
                } }, index)));
    };
    /* Pagination including negative values */
    const data = [];
    for (let i = firstPage - 1; i < lastPage; i += 1) {
        data.push(i + 1);
    }
    const activeIndex = data.indexOf(activePage);
    const paginationArray = [];
    const willNotUseEllipsis = data.length <= 7;
    if (willNotUseEllipsis) {
        for (let i = 0; i < data.length; i += 1) {
            paginationArray.push(page(data[i], i === activeIndex));
        }
    }
    else if (activeIndex < 4) {
        for (let i = 0; i < data.length; i += 1) {
            if (i === 5) {
                paginationArray.push(ellipsis(i + `${lastPage}`));
                paginationArray.push(page(lastPage, i === activeIndex));
                break;
            }
            paginationArray.push(page(data[i], i === activeIndex));
        }
    }
    else if (activeIndex < data.indexOf(lastPage) - 3) {
        paginationArray.push(page(firstPage, false));
        paginationArray.push(ellipsis(firstPage));
        for (let i = activeIndex - 1; i <= activeIndex + 1; i += 1) {
            paginationArray.push(page(data[i], i === activeIndex));
        }
        paginationArray.push(ellipsis(firstPage + `${lastPage}`));
        paginationArray.push(page(lastPage, false));
    }
    else {
        paginationArray.push(page(firstPage, false));
        paginationArray.push(ellipsis(firstPage));
        for (let i = data.indexOf(lastPage) - 4; i <= data.indexOf(lastPage); i += 1) {
            paginationArray.push(page(data[i], i === activeIndex));
        }
    }
    /** Arrows */
    const arrowDownDisabled = (activePage <= firstPage && !reverse) || (activePage >= lastPage && reverse);
    const arrowUpDisabled = (activePage >= lastPage && !reverse) || (activePage <= firstPage && reverse);
    const arrowDown = (React.createElement("li", { role: "presentation", className: cx("arrowDown", { disabled: arrowDownDisabled }) },
        React.createElement("a", { role: "presentation", className: "arrowDownClickable", onClick: (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!arrowDownDisabled)
                    handleDownClick();
            } },
            React.createElement("i", { className: "icon icon-arrow-down" }))));
    const arrowUp = (React.createElement("li", { role: "presentation", className: cx("arrowUp", { disabled: arrowUpDisabled }) },
        React.createElement("a", { role: "presentation", className: "arrowUpClickable", onClick: (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!arrowUpDisabled)
                    handleUpClick();
            } },
            React.createElement("i", { className: "icon icon-arrow-up" }))));
    const verticalPagination = vertical ? (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("pagination-group vertical", { disabled }, className) },
        React.createElement("ul", { className: cx("pagination", { reverse }) },
            reverse ? arrowUp : arrowDown,
            paginationArray,
            reverse ? arrowDown : arrowUp))) : null;
    const singleSelectItems = itemsPerPageOptions?.map((cur) => {
        return {
            title: cur,
            value: cur
        };
    });
    const actions = (React.createElement("div", { className: "actions" },
        showPageSelector && (React.createElement(React.Fragment, null,
            React.createElement("label", { className: "left" }, "Go to"),
            React.createElement(NumberField, { onChange: (value) => onPageChanged && onPageChanged(value), value: activePage, min: start || 1, max: end || pages }))),
        showItemsPerPageSelector && (React.createElement(React.Fragment, null,
            React.createElement("label", { className: "left" }, "Show"),
            React.createElement(SingleSelect, { items: singleSelectItems, label: currentItemsPerPage?.toString(), onChange: (item) => onItemsPerPageChanged && onItemsPerPageChanged(parseInt(item.value.toString())), placeholder: currentItemsPerPage?.toString(), type: "single" })))));
    const showSelectors = showPageSelector || showItemsPerPageSelector;
    const defaultPagination = (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("pagination-group", { disabled }, className) },
        React.createElement("ul", { className: "pagination" },
            React.createElement("li", { role: "presentation", onClick: handleLeftClick, className: cx("arrowLeft", { disabled: activePage <= firstPage }) },
                React.createElement("a", { role: "presentation", className: "arrowLeftClickable", onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleLeftClick();
                    } },
                    React.createElement("i", { className: "icon icon-arrow-left" }))),
            paginationArray,
            React.createElement("li", { role: "presentation", className: cx("arrowRight", { disabled: activePage >= lastPage }) },
                React.createElement("a", { role: "presentation", className: "arrowRightClickable", onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRightClick();
                    } },
                    React.createElement("i", { className: "icon icon-arrow-right" })))),
        showSelectors && actions));
    return !vertical ? defaultPagination : verticalPagination;
};

const ButtonGroup = ({ children, orientation, className, style, dataAttributes }) => {
    return (React.createElement("div", { className: `btn-group ${orientation} ${className}`, style: style, ...(dataAttributes && createDataAttributeObject(dataAttributes)) }, children));
};

const FileUpload = ({ title, onChange, className, style, disabled, accept, hideIcon = false, dataAttributes }) => {
    return (React.createElement("label", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("fileupload", { disabled }, className), style: style },
        React.createElement("input", { type: "file", onChange: (e) => onChange && onChange(e), accept: accept }),
        React.createElement("span", { className: "btn" },
            !hideIcon && React.createElement(Icon, { name: "upload" }),
            title !== undefined ? title : "Choose file")));
};

const TextField = ({ label, leftLabel, value, onChange, placeholder, focus, disabled, readonly, fullWidth, prefix, suffix, suffixInside, validation, valid, invalid, className, style, onBlur, onFocus, onKeyDown, onKeyPress, onKeyUp, required, dataAttributes }) => {
    // const { inputRef } = useFocus();
    const inputRef = useRef(null);
    const onChangeHandler = (e) => onChange && onChange(e.target.value);
    const [id] = useState(uniqueId());
    const prefixRef = useRef(null);
    const suffixRef = useRef(null);
    const labelRef = useRef(null);
    const [prefixWidth, setPrefixWidth] = useState(0);
    const [suffixWidth, setSuffixWidth] = useState(0);
    const [labelWidth, setLabelWidth] = useState(0);
    useEffect(() => {
        setPrefixWidth(prefixRef.current ? prefixRef.current.clientWidth : 0);
        setSuffixWidth(suffixRef.current ? suffixRef.current.offsetWidth : 10);
        setLabelWidth(labelRef.current && leftLabel ? labelRef.current.offsetWidth : 0);
    }, [prefixRef, suffixRef, labelRef, leftLabel]);
    const fullwidthFix = {
        ...style,
        width: `calc(100% - ${prefixWidth + suffixWidth + labelWidth}px)`
    };
    const hasLabelSpace = () => {
        if (inputRef !== undefined &&
            inputRef.current !== null &&
            value !== undefined &&
            value !== null) {
            const fontSize = parseInt(window.getComputedStyle(inputRef.current).getPropertyValue("font-size").replace("px", ""), 0);
            const width = inputRef.current.clientWidth;
            return ((value.length - 1) * fontSize) / 1.99 < width;
        }
        return true;
    };
    useEffect(() => {
        if (focus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [focus]);
    return (React.createElement(React.Fragment, null,
        label && (React.createElement("label", { ref: labelRef, htmlFor: id, className: cx({ left: leftLabel }, { required }) }, label)),
        prefix && (React.createElement("div", { ref: prefixRef, className: "prefix" }, prefix)),
        React.createElement("input", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), ref: inputRef, title: !hasLabelSpace() ? value : undefined, id: id, style: fullWidth && (prefix || suffix) ? fullwidthFix : style, type: "text", className: cx(fullWidth ? "fullwidth" : "", { fullwidthWithPrefix: fullWidth && (prefix || suffix) }, className, { "validation invalid": invalid }, { "validation valid": valid }), placeholder: placeholder || "", value: value, onChange: onChangeHandler, onBlur: (e) => onBlur && onBlur(e), onKeyDown: (e) => onKeyDown && onKeyDown(e), onKeyUp: (e) => onKeyUp && onKeyUp(e), onKeyPress: (e) => onKeyPress && onKeyPress(e), onFocus: (e) => onFocus && onFocus(e), disabled: disabled, readOnly: readonly }),
        suffix && (React.createElement("div", { ref: suffixRef, className: cx("suffix", { "icon-inside": suffixInside }) }, suffix)),
        validation !== undefined && value !== "" && (React.createElement("span", { className: cx("validation-msg", { invalid }) }, validation))));
};

const TextArea = ({ value, onChange, onBlur, onKeyDown, onKeyPress, onKeyUp, placeholder, disabled, readonly, className, style, dataAttributes }) => {
    const onChangeHandler = (e) => onChange && onChange(e.target.value);
    return (React.createElement("textarea", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), placeholder: placeholder !== undefined ? placeholder : "", value: value, onChange: onChangeHandler, onBlur: (e) => onBlur && onBlur(e), onKeyDown: (e) => onKeyDown && onKeyDown(e), onKeyUp: (e) => onKeyUp && onKeyUp(e), onKeyPress: (e) => onKeyPress && onKeyPress(e), disabled: disabled, readOnly: readonly, className: cx(className), style: style }));
};

var SortingDirection;
(function (SortingDirection) {
    SortingDirection[SortingDirection["None"] = 0] = "None";
    SortingDirection[SortingDirection["Ascending"] = 1] = "Ascending";
    SortingDirection[SortingDirection["Descending"] = 2] = "Descending";
})(SortingDirection || (SortingDirection = {}));

const Table = ({ columns, rows, size, rowsStyle, onRowClick, onCellClick, className, selectable = false, selectedRows, onSort, disabled, style, dataAttributes, tableInfo, pagination, onEditedCell, splitCellStyle, scrollHorizontal, scrollVertical, showSettingsIcon, onSettingsIconClick, topButtons, hideHeader }) => {
    const [editingColumns, setEditingColumns] = useState([]);
    const [editingColumnsValue, setEditingColumnsValue] = useState({});
    const setColumnValue = (key, value) => {
        const currentEditingColumnsValue = JSON.parse(JSON.stringify(editingColumnsValue));
        currentEditingColumnsValue[key] = value;
        setEditingColumnsValue(currentEditingColumnsValue);
    };
    const handleClick = (key, sortingDirection) => {
        if (sortingDirection === SortingDirection.None && onSort) {
            onSort(key, SortingDirection.Ascending);
        }
        else if (sortingDirection === SortingDirection.Ascending && onSort) {
            onSort(key, SortingDirection.Descending);
        }
        else if (sortingDirection === SortingDirection.Descending && onSort) {
            onSort(key, SortingDirection.None);
        }
    };
    const showTableInfo = () => {
        return (React.createElement("div", { className: "table-top" },
            React.createElement("div", { className: "table-top-left" },
                React.createElement("div", { className: "table-info" },
                    React.createElement("span", { className: "count-show" }, ` ${tableInfo?.show.from} - ${tableInfo?.show.to} `),
                    React.createElement("span", { className: "count-total" },
                        "of ",
                        tableInfo?.total,
                        " items"),
                    React.createElement("span", { className: "count-selected" },
                        "| ",
                        tableInfo?.selected,
                        " selected")))));
    };
    const showTableSettings = () => {
        return (React.createElement(React.Fragment, null,
            React.createElement("button", { className: "btn-icon table-settings", title: "Manage columns", onClick: () => onSettingsIconClick && onSettingsIconClick() },
                React.createElement("i", { className: "icon icon-settings" }))));
    };
    const usePagination = React.createElement("div", { className: "table-bottom" }, pagination);
    const ScrollWrapper = ({ children }) => {
        if (scrollVertical) {
            return (React.createElement("div", { className: "table-scroll sticky", style: { height: "100%" } }, children));
        }
        if (scrollHorizontal) {
            return (React.createElement("div", { className: "table-scroll", style: { width: "100%" } }, children));
        }
        return children;
    };
    const tableComponent = (React.createElement(React.Fragment, null,
        React.createElement("table", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("table", size !== "normal" ? size : "", rowsStyle !== "none" ? rowsStyle : "", {
                sortable: columns.filter((c) => c.sortable).length > 0,
                disabled
            }, splitCellStyle !== "none" ? `split-${splitCellStyle}` : "", className), style: style },
            !hideHeader && (React.createElement("thead", null,
                React.createElement("tr", null, columns.map(c => (React.createElement("th", { key: `h_${c.key}`, className: cx({ "is-sortable": c.sortable }, { asc: c.sortingDirection === SortingDirection.Ascending }, {
                        desc: c.sortingDirection === SortingDirection.Descending
                    }, { freeze: c.freeze }), onClick: () => c.sortable && onSort
                        ? handleClick(c.key, c.sortingDirection !== undefined
                            ? c.sortingDirection
                            : SortingDirection.None)
                        : null, style: c.freeze ? { left: "0px" } : {} }, c.header)))))),
            React.createElement("tbody", null, rows.map((r, rowIndex) => {
                const key = `r_${rowIndex}`;
                return (React.createElement("tr", { key: key, className: cx({
                        selected: selectable && selectedRows?.includes(r.key)
                    }) }, columns.map((c, columnIndex) => {
                    const renderCell = (label, columnKey) => {
                        if (c.editable) {
                            if (editingColumns.includes(columnKey)) {
                                return (React.createElement("div", { className: "editing", onClick: (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    } },
                                    React.createElement("div", { className: "inline-field" },
                                        React.createElement(TextField, { focus: true, onChange: (e) => {
                                                setColumnValue(columnKey, e);
                                            }, value: typeof editingColumnsValue[columnKey] !== "undefined"
                                                ? editingColumnsValue[columnKey]
                                                : label })),
                                    React.createElement("div", { className: "inline-buttons" },
                                        React.createElement("button", { className: "btn-icon", "data-action": "cancel", onClick: () => {
                                                const currentEditingColumns = editingColumns.slice(0);
                                                currentEditingColumns.splice(currentEditingColumns.indexOf(columnKey), 1);
                                                setEditingColumns(currentEditingColumns);
                                                setColumnValue(columnKey, label);
                                            } },
                                            React.createElement("i", { className: "icon icon-cross" })),
                                        React.createElement("button", { className: "btn-icon", "data-action": "submit", onClick: () => {
                                                if (onEditedCell) {
                                                    const editedCell = JSON.parse(JSON.stringify(r));
                                                    editedCell[c.key] = editingColumnsValue[columnKey]
                                                        ? editingColumnsValue[columnKey]
                                                        : label;
                                                    onEditedCell(editedCell, rowIndex);
                                                }
                                                const currentEditingColumns = editingColumns.slice(0);
                                                currentEditingColumns.splice(currentEditingColumns.indexOf(columnKey), 1);
                                                setEditingColumns(currentEditingColumns);
                                            } },
                                            React.createElement("i", { className: "icon icon-check" })))));
                            }
                            return React.createElement("div", { className: "editable" }, label);
                        }
                        if (typeof label === "string" || typeof label === "number") {
                            return label;
                        }
                        else {
                            // @ts-ignore: This is not valid typescript
                            return Object.values(label).map((l) => React.createElement("div", { key: l }, l));
                        }
                    };
                    const handleRowClick = (r, columnKey = "", editable = false) => {
                        if (editable) {
                            const currentEditingColumns = editingColumns.slice(0);
                            currentEditingColumns.push(columnKey);
                            setEditingColumns(currentEditingColumns);
                        }
                        if (onRowClick) {
                            onRowClick(r);
                        }
                        if (onCellClick) {
                            onCellClick(r, rowIndex, columnIndex);
                        }
                    };
                    return (React.createElement("td", { role: "presentation", onClick: () => handleRowClick(r, `r_${key}_${c.key}`, c.editable), key: `r_${key}_${c.key}`, className: `${c.editable ? "cell-edit" : ""}
                      ${c.enableDropdownMenu ? "cell-actions" : ""}
                      ${typeof r[c.key] !== "string" && typeof r[c.key] !== "number" && !c.Cell
                            ? "cell-split"
                            : ""} ${c.freeze ? "freeze" : ""}
                      `, style: c.freeze ? { whiteSpace: "nowrap", left: "0px" } : {} }, c.Cell ? c.Cell(r) : renderCell(r[c.key], `r_${key}_${c.key}`)));
                })));
            })))));
    return (React.createElement(React.Fragment, null,
        (tableInfo || showSettingsIcon || topButtons) && (React.createElement("div", { className: "table-top" },
            tableInfo && showTableInfo(),
            React.createElement("div", { className: "table-top-right" },
                topButtons,
                showSettingsIcon && React.createElement("div", { className: "table-actions" }, showTableSettings())))),
        React.createElement(ScrollWrapper, null, tableComponent),
        pagination && usePagination));
};

const Dialog = ({ title, subtitle, buttons, fullscreen, className, contentClassName, onClose, children, visible = true, style, closeOnEscape, dataAttributes }) => {
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key === "Escape" && closeOnEscape) {
                if (onClose) {
                    onClose();
                }
            }
            return null;
        };
        if (closeOnEscape) {
            window.addEventListener("keydown", handleKeyPress);
            return () => {
                window.removeEventListener("keydown", handleKeyPress);
            };
        }
        return undefined;
    }, [closeOnEscape, onClose]);
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("dialog", { fullscreen }, { show: visible }) },
        React.createElement("div", { className: cx("content", className), style: style },
            React.createElement("div", { className: "top" },
                React.createElement("div", { className: "title" }, title),
                React.createElement("div", { className: "subtitle" }, subtitle),
                fullscreen && (React.createElement("div", { role: "presentation", onClick: () => onClose && onClose(), className: "right" },
                    " ",
                    React.createElement("i", { className: "icon icon-cross", "data-close": "true" })))),
            React.createElement("div", { className: cx("body", contentClassName) }, children),
            buttons && React.createElement("div", { className: "bottom" }, buttons))));
};

const Checkbox = ({ text, checked, onChange, disabled, indeterminate, className, style, dataAttributes }) => {
    const id = uniqueId();
    const stopBubbling = (e) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("input", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), type: "checkbox", id: id, checked: checked && !indeterminate, onClick: stopBubbling, ref: input => {
                const Input = input;
                if (Input) {
                    Input.indeterminate = indeterminate;
                }
            }, onChange: (e) => {
                if (onChange) {
                    onChange(!checked, e);
                }
            }, disabled: disabled, className: cx("checkbox", className), style: style }),
        React.createElement("label", { onClick: stopBubbling, className: cx("chklabel", className), htmlFor: id }, text)));
};

const CheckboxGroup = ({ children, validationMessage }) => {
    return (React.createElement("div", { className: "checkbox-group" },
        children,
        validationMessage && React.createElement("span", { className: "validation-msg" }, validationMessage)));
};

const getYear = (date) => {
    return date.toLocaleString("en-us", { year: "numeric" });
};
const getMonth = (date, numeric = false) => {
    let month = date.toLocaleString("en-us", {
        month: numeric ? "numeric" : "long"
    });
    if (numeric) {
        if (date.getMonth() + 1 < 9) {
            month = `0${month}`;
        }
        else {
            return month;
        }
    }
    return month;
};
const changeDate = (date, numOfmonths) => {
    return new Date(date.setMonth(date.getMonth() + numOfmonths));
};
const isEqual = (d1, d2) => {
    if (d1 === undefined || d2 === undefined || d1 === null || d2 === null) {
        return false;
    }
    return (d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate());
};
const formatJsDate = (jsDate) => {
    const mm = jsDate.getMonth() + 1;
    const dd = jsDate.getDate();
    return [jsDate.getFullYear(), (mm > 9 ? "" : "0") + mm, (dd > 9 ? "" : "0") + dd].join("-");
};

const CalendarBodyContent = (visibleDate, onDaySelected, selectedDate, minDate, maxDate) => {
    const dArray = formatJsDate(visibleDate).split("-");
    const jsDate = new Date(parseInt(dArray[0]), parseInt(dArray[1]) - 1, 1);
    const lastDayMonth = new Date(parseInt(dArray[0]), parseInt(dArray[1]), 0);
    const daysInMonthNum = lastDayMonth.getDate();
    const dayOfWeekNum = jsDate.getDay();
    const headZeroes = Array.apply(null, {
        length: (dayOfWeekNum + 6) % 7
    }).map(Number.prototype.valueOf, 0);
    // create day numbers != 0
    const numbersInRow = Array.apply(null, {
        length: daysInMonthNum + 1
    }).map(Number.call, Number);
    numbersInRow.shift();
    // create 0's at the tail
    const numZeroes = 42 - (headZeroes.length + numbersInRow.length);
    const tailZeroes = Array.apply(null, Array(numZeroes)).map(Number.prototype.valueOf, 0);
    // concat all the days and add to calendar
    const calendarDays = headZeroes.concat(numbersInRow, tailZeroes);
    const nWeekDays = 7;
    // 7 days / week
    const wNeeks = 6;
    // 6 weeks / month
    const weeks = [];
    let currentCalendarDayIndex = 0;
    for (let i = 0; i < wNeeks; i++) {
        const createWeekdays = () => {
            const weekDays = [];
            for (let j = 0; j < nWeekDays; j++) {
                const currentDate = new Date(`${getYear(visibleDate)}-${getMonth(visibleDate, true)}-${calendarDays[currentCalendarDayIndex]}`);
                const isSelected = isEqual(selectedDate, currentDate);
                const isToday = isEqual(new Date(), currentDate);
                const isDisabled = (minDate && currentDate < minDate) || (maxDate && currentDate > maxDate);
                const index = j;
                const dayValue = calendarDays[currentCalendarDayIndex];
                weekDays.push(React.createElement("td", { className: cx({ selected: isSelected }, { dummy: dayValue === 0 }, { disabled: isDisabled }), key: index, role: "gridcell", onClick: () => dayValue > 0 && !isDisabled && onDaySelected(currentDate) }, isToday ? (React.createElement("span", { className: "today" }, dayValue !== 0 && calendarDays[currentCalendarDayIndex])) : (dayValue !== 0 && calendarDays[currentCalendarDayIndex])));
                currentCalendarDayIndex++;
            }
            return weekDays;
        };
        const index = i;
        weeks.push(React.createElement("tr", { key: index }, createWeekdays()));
    }
    return weeks;
};
const DateCalendar = ({ visible, visibleDate, selectedDate, onVisibleDateChanged, onDateSelected, className, todayButton, onTodayClick, minDate, maxDate, style, disabled, dataAttributes }) => {
    const handleTodayClick = () => {
        const currentDate = new Date();
        onVisibleDateChanged(currentDate);
        if (onTodayClick) {
            onTodayClick();
        }
    };
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("calendar", { closed: !visible, disabled }, className), style: style },
        React.createElement("div", { className: "head" },
            React.createElement(Icon, { name: "chevron-left", onClick: () => onVisibleDateChanged(changeDate(visibleDate, -1)) }),
            React.createElement("span", { className: "month" }, getMonth(visibleDate)),
            React.createElement(Icon, { name: "chevron-right", onClick: () => onVisibleDateChanged(changeDate(visibleDate, 1)) }),
            React.createElement(Icon, { name: "chevron-left", onClick: () => onVisibleDateChanged(changeDate(visibleDate, -12)) }),
            React.createElement("span", { className: "year" }, getYear(visibleDate)),
            React.createElement(Icon, { name: "chevron-right", onClick: () => onVisibleDateChanged(changeDate(visibleDate, 12)) })),
        React.createElement("table", { className: "body" },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", null, "Mo"),
                    React.createElement("th", null, "Tu"),
                    React.createElement("th", null, "We"),
                    React.createElement("th", null, "Th"),
                    React.createElement("th", null, "Fr"),
                    React.createElement("th", null, "Sa"),
                    React.createElement("th", null, "Su"))),
            React.createElement("tbody", null, CalendarBodyContent(visibleDate, onDateSelected, selectedDate, minDate, maxDate))),
        todayButton && (React.createElement("div", { className: "calendar-footer" },
            React.createElement("div", { className: "btn set-current-day", onClick: handleTodayClick }, "Today")))));
};

const DateField = ({ selectedDate, onChange, onDateSelected, onBlur, className, disabled = false, format = "YYYY-MM-DD", separator = "-", todayButton = false, onTodayClick, minDate, maxDate, style, dataAttributes }) => {
    const [toggleCalendar, setToggleCalendar] = useState(false);
    const closeCalendar = () => setToggleCalendar(false);
    const [inputValue, setInputValue] = useState("");
    const [visibleDate, setVisibleDate] = useState(new Date());
    const dateRegExpByFormat = () => {
        switch (format) {
            case "DD-MM-YYYY":
            case "MM-DD-YYYY":
                return `([0-9]{2})${separator}([0-9]{2})${separator}([0-9]{4})`;
            default:
                return `([0-9]{4})${separator}([0-9]{2})${separator}([0-9]{2})`;
        }
    };
    const createDateByFormat = (g1, g2, g3) => {
        switch (format) {
            case "DD-MM-YYYY":
                return new Date(`${g3}-${g2}-${g1}`);
            case "MM-DD-YYYY":
                return new Date(`${g3}-${g1}-${g2}`);
            default:
                return new Date(`${g1}-${g2}-${g3}`);
        }
    };
    const yearMonthDayDateStringByFormat = (dateString) => {
        if (dateString.length !== 10) {
            return "";
        }
        switch (format) {
            case "DD-MM-YYYY":
                return `${dateString.slice(6, 10)}-${dateString.slice(3, 5)}-${dateString.slice(0, 2)}`;
            case "MM-DD-YYYY":
                return `${dateString.slice(6, 10)}-${dateString.slice(0, 2)}-${dateString.slice(3, 5)}`;
            default:
                return `${dateString.slice(0, 4)}-${dateString.slice(5, 7)}-${dateString.slice(8, 10)}`;
        }
    };
    const datePlaceholderByFormat = () => {
        switch (format) {
            case "DD-MM-YYYY":
                return `DD${separator}MM${separator}YYYY`;
            case "MM-DD-YYYY":
                return `MM${separator}DD${separator}YYYY`;
            default:
                return `YYYY${separator}MM${separator}DD`;
        }
    };
    useEffect(() => {
        const dateStringByFormat = (date) => {
            const year = date.getFullYear().toString();
            const month = ("0" + (date.getMonth() + 1)).toString().slice(-2);
            const day = ("0" + date.getDate()).toString().slice(-2);
            switch (format) {
                case "DD-MM-YYYY":
                    return `${day}${separator}${month}${separator}${year}`;
                case "MM-DD-YYYY":
                    return `${month}${separator}${day}${separator}${year}`;
                default:
                    return `${year}${separator}${month}${separator}${day}`;
            }
        };
        if (selectedDate) {
            setInputValue(dateStringByFormat(selectedDate));
        }
        else {
            setInputValue("");
        }
    }, [selectedDate, separator, format]);
    const isValidDate = (dateString) => {
        const regEx = new RegExp(dateRegExpByFormat());
        // Invalid format
        if (!dateString.match(regEx)) {
            return false;
        }
        const match = regEx.exec(dateString);
        if (!match) {
            return false;
        }
        const d = createDateByFormat(match[1], match[2], match[3]);
        // Invalid date (or this could be epoch)
        if (!(d || 0)) {
            return false;
        }
        return d.toISOString().slice(0, 10) === yearMonthDayDateStringByFormat(dateString);
    };
    const updateInputValue = (typedInput) => {
        if (onChange) {
            onChange(typedInput);
        }
        else {
            setInputValue(typedInput);
        }
        if (typedInput.length === 10) {
            if (isValidDate(typedInput)) {
                const newDate = new Date(yearMonthDayDateStringByFormat(typedInput));
                if ((minDate && newDate < minDate) || (maxDate && newDate > maxDate)) {
                    return;
                }
                if (onDateSelected) {
                    onDateSelected(newDate);
                }
            }
        }
        setInputValue(typedInput);
    };
    const containerRef = useClickOutside(closeCalendar);
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), ref: containerRef, className: cx("datepicker", { disabled }, className) },
        React.createElement("input", { type: "text", placeholder: datePlaceholderByFormat(), name: "datepicker", maxLength: 10, className: "with-icon", style: style, value: inputValue, autoComplete: "off", onBlur: (e) => onBlur && onBlur(e), onChange: (e) => !disabled && updateInputValue(e.target.value), onClick: () => setToggleCalendar(true) }),
        React.createElement("div", { className: "suffix icon-inside" },
            React.createElement(Icon, { name: "calendar", onClick: () => setToggleCalendar(!toggleCalendar) })),
        React.createElement(DateCalendar, { visible: toggleCalendar, visibleDate: visibleDate, selectedDate: selectedDate, onVisibleDateChanged: (date) => setVisibleDate(date), onDateSelected: (date) => {
                if (onDateSelected) {
                    onDateSelected(date);
                }
                closeCalendar();
            }, todayButton: todayButton, onTodayClick: onTodayClick, minDate: minDate, maxDate: maxDate })));
};

const Dropdown = ({ items, label, right, onItemClick, disabled, primary, divided, className, style, dataAttributes }) => {
    const [visible, setVisible] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const openDropdown = () => {
        setVisible(true);
    };
    const closeDropdown = () => setVisible(false);
    const containerRef = useClickOutside(closeDropdown);
    const itemPicked = (itemIndex) => {
        if (!onItemClick) {
            return;
        }
        closeDropdown();
        onItemClick(items[itemIndex]);
    };
    const handleButtonClick = () => (visible ? closeDropdown() : openDropdown());
    const handleContainerKeyPress = (e) => {
        const { keyCode } = e;
        if (!visible) {
            // menu open by eds on enter
            if (keyCode === 40) {
                openDropdown();
            }
        }
        else {
            switch (keyCode) {
                case 13:
                    // menu closed by eds
                    itemPicked(highlighted);
                    break;
                case 38:
                    setHighlighted(highlighted > 0 ? highlighted - 1 : items.length - 1);
                    break;
                case 40:
                    setHighlighted(highlighted < items.length - 1 ? highlighted + 1 : 0);
                    break;
            }
        }
    };
    const handleMenuClick = (index) => {
        itemPicked(index);
    };
    const ignoreEvent = () => { };
    const renderItem = (items, index) => {
        return !items.separator ? (React.createElement("div", { key: uniqueId(items.title), className: cx("item", { disabled: items.disabled }, { indent: items.indent }, { header: items.header }), role: "button", tabIndex: 0, onClick: e => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                handleMenuClick(index);
            }, onKeyDown: ignoreEvent }, items.title)) : (React.createElement("hr", { key: index }));
    };
    const divType = () => {
        if (divided) {
            return "divided";
        }
        return "click";
    };
    const buttonType = !divided ? (React.createElement("button", { type: "button", className: cx("btn clickable", { primary }), onClick: e => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            handleButtonClick();
        }, disabled: disabled }, label || "")) : (React.createElement("div", { className: "btns-wrap" },
        React.createElement("button", { type: "button", className: "btn", onClick: e => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                itemPicked(0);
            }, disabled: disabled }, items[0].title || ""),
        React.createElement("button", { type: "button", className: "btn extra", disabled: disabled, onClick: e => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                handleButtonClick();
            } })));
    const isOutOfViewport = (elem) => {
        const itemHeightOffset = 32;
        const maxOffset = 130;
        const offsetHeight = items.length < 4 ? itemHeightOffset * items.length : maxOffset;
        const bounding = elem.getBoundingClientRect();
        return (bounding.bottom + offsetHeight > (window.innerHeight || document.documentElement.clientHeight));
    };
    const outOfViewport = containerRef.current ? isOutOfViewport(containerRef.current) : false;
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), id: "dropdown", ref: containerRef, className: cx("dropdown", { open: visible }, { closed: !visible }, className), style: style, "data-type": divType(), role: "button", tabIndex: 0, onKeyDown: handleContainerKeyPress },
        buttonType,
        React.createElement("div", { className: cx("menu dropdown-height", { visible }, { right }, { above: outOfViewport }) }, items.map((i, index) => {
            return renderItem(i, index);
        }))));
};

const DropdownMenu = ({ menuItems, onChange, className, style, onClick, disabled, dataAttributes }) => {
    const [visible, setVisible] = useState(false);
    const closeDropdown = () => setVisible(false);
    const containerRef = useClickOutside(closeDropdown);
    const handleClick = (index) => {
        const menuItem = menuItems[index];
        if (onChange !== undefined) {
            onChange(menuItems[index]);
        }
        if (menuItem.onItemClick && !menuItem.disabled) {
            menuItem.onItemClick();
        }
    };
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), ref: containerRef, role: "presentation", className: cx("dropdown more", { open: visible, disabled }, className), style: style, "data-type": "click", onClick: () => {
            if (onClick) {
                onClick(!visible);
            }
            setVisible(!visible);
        } },
        React.createElement("button", { type: "button", className: "btn clickable" },
            React.createElement("i", { className: "icon icon-more" })),
        React.createElement("div", { className: cx("menu", { visible }) }, menuItems.map((menuItem, index) => {
            const keyIndex = index;
            return !menuItem.separator ? (React.createElement("div", { key: keyIndex, role: "presentation", onClick: () => handleClick(index), className: cx("item", { "space-between": menuItem.spacebetween }, { disabled: menuItem.disabled }), ...(menuItem.dataAttributes && createDataAttributeObject(menuItem.dataAttributes)) }, menuItem.item)) : (React.createElement("hr", { key: keyIndex }));
        }))));
};

const Radiobutton = ({ text, value, disabled, onChange, className, style, dataAttributes }) => {
    const id = uniqueId();
    return (React.createElement(React.Fragment, null,
        React.createElement("input", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx(className), style: style, disabled: disabled, onChange: () => {
                if (!disabled && onChange) {
                    onChange(!value);
                }
            }, id: id, type: "radio", checked: value }),
        React.createElement("label", { className: cx(className), htmlFor: id }, text)));
};

const Tabs = ({ tabs, className, contentRef, children, onChange, disabled, style, dataAttributes, renderMode = "loadAll" }) => {
    const selectedTabIndex = tabs
        ?.map((t, i) => {
        if (t.selected) {
            return i;
        }
        return -1;
    })
        .filter(a => a !== -1);
    const [selectedTab, setSelected] = useState(selectedTabIndex && selectedTabIndex.length > 0 ? selectedTabIndex[0] : 0);
    const [renderedTabs, setRenderedTabs] = useState([selectedTab]);
    const [tabKey] = useState(uniqueId());
    const handleTabSelected = (i) => {
        setSelected(i);
        setRenderedTabs(current => {
            if (current.indexOf(i) >= 0) {
                return current;
            }
            return [i, ...current];
        });
        const callBack = (tabs && tabs[i].onSelected) || (() => { });
        callBack();
    };
    const titles = children &&
        React.Children.map(children, (child, idx) => {
            const { props } = child;
            const fixedProps = props;
            return (React.createElement("div", { key: `${tabKey}-m-${fixedProps.title}`, role: "button", tabIndex: 0, className: cx("title", {
                    selected: fixedProps.selected,
                    disabled: fixedProps.disabled
                }), onClick: () => {
                    if (onChange) {
                        onChange(idx);
                    }
                    if (fixedProps.onSelected) {
                        fixedProps.onSelected();
                    }
                }, onKeyDown: () => { } }, fixedProps.title));
        });
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("tabs", { disabled }, className), style: style },
        React.createElement("div", { className: "titles" },
            tabs &&
                tabs.map((t, idx) => (React.createElement("div", { key: `${tabKey}-m-${t.title}`, role: "button", tabIndex: 0, className: cx("title", {
                        selected: idx === selectedTab,
                        disabled: t.disabled
                    }), onClick: () => handleTabSelected(idx), onKeyDown: () => { } }, t.title))),
            children && titles),
        React.createElement("div", { className: "contents", ref: contentRef },
            tabs &&
                tabs.map((t, idx) => (React.createElement("div", { key: `${tabKey}-c-${t.title}`, className: cx("content", { selected: idx === selectedTab }) }, renderMode === "lazyLoad" && renderedTabs.indexOf(idx) < 0 ? (React.createElement(React.Fragment, null)) : (t.render && t.render())))),
            children)));
};

const Tab = ({ selected, children, disabled, dataAttributes }) => {
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("content", { selected, disabled }) }, children));
};

const WizardSteps = ({ titles, currentStep, completedPages, onStepClick, visibleSteps, onLeftClick, onRightClick, stepLeftDisabled = false, stepRightDisabled = false, stepsDisabled = false, hideStepLeft = false, hideStepRight = false, hideSteps = false }) => {
    const arrowLeft = (React.createElement("div", { role: "presentation", style: {
            visibility: !visibleSteps.includes(titles[0]) ? "visible" : "hidden"
        }, className: cx("arrowLeft arrow left visible", {
            noHover: stepLeftDisabled
        }), onClick: () => !stepLeftDisabled && onLeftClick() },
        React.createElement("i", { className: "icon icon-arrow-left" })));
    const arrowRight = (React.createElement("div", { role: "presentation", style: {
            visibility: !visibleSteps.includes(titles[titles.length - 1]) ? "visible" : "hidden"
        }, className: cx("arrowRight arrow right visible", {
            noHover: stepRightDisabled
        }), onClick: () => !stepRightDisabled && onRightClick() },
        React.createElement("i", { className: "icon icon-arrow-right" })));
    const steps = titles.map((title, i) => {
        const isCompleted = completedPages.includes(i);
        const isCurrentPage = i === currentStep;
        const isVisible = visibleSteps.includes(titles[i]);
        return (React.createElement("div", { key: uniqueId(), role: "presentation", className: cx("step", { current: isCurrentPage }, { completed: isCompleted }, { visible: isVisible }, { hidden: !isVisible }, { noHover: stepsDisabled }), onClick: () => (isCompleted && !stepsDisabled ? onStepClick(i) : undefined) },
            React.createElement("div", { className: "prevbar" }),
            React.createElement("div", { className: "nextbar" }),
            React.createElement("div", { className: "sphere" }),
            React.createElement("div", { className: "description" },
                i + 1,
                ". ",
                title)));
    });
    return !hideSteps ? (React.createElement("div", { className: "wizard-steps" },
        !hideStepLeft && arrowLeft,
        React.createElement("div", { className: "steps" }, steps),
        !hideStepRight && arrowRight)) : (React.createElement("span", null));
};

const WizardFooter = ({ lastStep, onNextClick, onPreviousClick, onFinishClick, onCancelClick, onSaveClick, finished = false, disablePrevious = false, disableNext = false, disableFinish = false, disableCancel = false, disableSave = false, hidePrevious = false, hideNext = false, hideCancel = false, hideSave = false, hideFinish = false, labelCancel, labelSave, labelPrevious, labelNext, labelFinish, labelFinished, leftAlignedButtons, rightAlignedButtons }) => {
    const defaultLabel = {
        cancel: "Cancel",
        save: "Save & finish later",
        previous: "Previous",
        next: labelNext !== "" ? labelNext : "Next",
        finish: labelFinish !== "" ? labelFinish : "Finish",
        finished: labelFinished !== "" ? labelFinished : "Finished"
    };
    const label = {
        cancel: labelCancel !== "" ? labelCancel : defaultLabel.cancel,
        save: labelSave !== "" ? labelSave : defaultLabel.save,
        previous: labelPrevious !== "" ? labelPrevious : defaultLabel.previous,
        next: labelNext !== "" ? labelNext : defaultLabel.next,
        finish: labelFinish !== "" ? labelFinish : defaultLabel.finish,
        finished: labelFinished !== "" ? labelFinished : defaultLabel.finished
    };
    const leftButtonGroup = (React.createElement("div", { className: "item" },
        !hideCancel && (React.createElement(Button, { disabled: disableCancel, onClick: onCancelClick }, label.cancel)),
        !hideSave && (React.createElement(Button, { disabled: disableSave, onClick: onSaveClick }, label.save))));
    const btnNext = !hideNext && (React.createElement(Button, { className: "btnNext", disabled: disableNext, primary: true, onClick: onNextClick },
        React.createElement("span", { className: "term" }, label.next),
        React.createElement("i", { className: "icon icon-arrow-right" })));
    const btnFinish = !hideFinish && (React.createElement(Button, { className: "btnFinish", disabled: disableFinish, primary: true, onClick: onFinishClick },
        React.createElement("span", { className: cx({ term: !lastStep }) }, !finished ? label.finish : label.finished)));
    const rightButtonGroup = (React.createElement("div", { className: "item" },
        !hidePrevious && (React.createElement(Button, { disabled: disablePrevious, onClick: onPreviousClick },
            React.createElement("i", { className: "icon icon-arrow-left" }),
            React.createElement("span", { className: "term" }, label.previous))),
        !lastStep ? btnNext : btnFinish));
    return (React.createElement("div", { className: "wizard-footer" },
        !leftAlignedButtons ? leftButtonGroup : React.createElement("div", { className: "item" }, leftAlignedButtons),
        !rightAlignedButtons ? rightButtonGroup : React.createElement("div", { className: "item" }, rightAlignedButtons)));
};

const WizardPageWrapper = ({ children, pageNumber, maxPages }) => {
    const childrenWithProps = React.Children.map(children, child => React.cloneElement(child, {
        page: pageNumber,
        totalPages: maxPages
    }));
    return React.createElement("div", { className: "wizard-content" }, childrenWithProps);
};

const Wizard = ({ children, titles, completedSteps, onCancel, onSave, onFinish, onPreviousPage, onNextPage, onPageChange, onStepLeftClick, onStepRightClick, className, disablePreviousButton = false, disableNextButton = false, disableFinishButton = false, disableCancelButton = false, disableSaveButton = false, disableStepLeftButton = false, disableStepRightButton = false, pageId, finished = false, leftAlignedButtons, rightAlignedButtons, disableSteps = false, hideStepLeftButton = false, hideStepRightButton = false, hideSteps = false, hideCancelButton = false, hideSaveButton = false, hidePreviousButton = false, hideNextButton = false, hideFinishButton = false, labelCancelButton = "", labelSaveButton = "", labelPreviousButton = "", labelNextButton = "", labelFinishButton = "", labelFinishedButton = "", style, dataAttributes }) => {
    const nPages = React.Children.count(children);
    const pages = children !== undefined && children !== null ? children : [];
    const Titles = titles !== undefined
        ? titles.slice()
        : React.Children.map(children, (element) => {
            return element.props.title;
        }).slice();
    const pageSteps = isMobileDevice() ? 1 : 4;
    const pageChunks = chunkArray(Titles.slice(), isMobileDevice() ? 1 : pageSteps);
    const [pageChunkId, setPageChunkId] = useState(0);
    const [visibleSteps, setVisibleSteps] = useState(pageChunks[pageChunkId]);
    const getVisibleSteps = (pageTitle) => pageChunks.find((pageChunk) => pageChunk.find((currentTitle) => pageTitle === currentTitle));
    const getPageChunkId = () => {
        let id = -1;
        pageChunks.forEach((pageChunk, i) => {
            if (compareArray(visibleSteps, pageChunk)) {
                id = i;
            }
        });
        return id;
    };
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("wizard", className), style: style },
        React.createElement(WizardSteps, { titles: Titles, currentStep: pageId >= 0 && pageId < nPages ? pageId : 0, completedPages: completedSteps, visibleSteps: visibleSteps, stepLeftDisabled: disableStepLeftButton, stepRightDisabled: disableStepRightButton, stepsDisabled: disableSteps, hideStepLeft: hideStepLeftButton, hideStepRight: hideStepRightButton, hideSteps: hideSteps, onStepClick: (clickedId) => onPageChange && onPageChange(clickedId), onLeftClick: () => {
                const chunkId = getPageChunkId();
                if (chunkId > 0) {
                    setPageChunkId(chunkId - 1);
                    setVisibleSteps(pageChunks[chunkId - 1]);
                }
                if (onStepLeftClick) {
                    onStepLeftClick();
                }
            }, onRightClick: () => {
                if (pageChunkId < nPages) {
                    setPageChunkId(pageChunkId + 1);
                    setVisibleSteps(pageChunks[pageChunkId + 1]);
                }
                if (onStepRightClick) {
                    onStepRightClick();
                }
            } }),
        React.createElement(WizardPageWrapper, { pageNumber: pageId + 1, maxPages: nPages }, pages[pageId]),
        React.createElement(WizardFooter, { finished: finished, lastStep: pageId === nPages - 1, disablePrevious: disablePreviousButton, disableNext: disableNextButton, disableFinish: disableFinishButton, disableCancel: disableCancelButton, disableSave: disableSaveButton, hideCancel: hideCancelButton, hideSave: hideSaveButton, hidePrevious: pageId === 0 || hidePreviousButton, hideNext: hideNextButton, hideFinish: hideFinishButton, labelCancel: labelCancelButton, labelSave: labelSaveButton, labelPrevious: labelPreviousButton, labelNext: labelNextButton, labelFinish: labelFinishButton, labelFinished: labelFinishedButton, onNextClick: () => {
                if (pageId + 1 < nPages) {
                    setVisibleSteps(getVisibleSteps(Titles[pageId + 1]));
                    setPageChunkId(getPageChunkId());
                }
                if (onNextPage) {
                    onNextPage(pageId + 1, pageId);
                }
            }, onPreviousClick: () => {
                if (pageId - 1 >= 0) {
                    setVisibleSteps(getVisibleSteps(Titles[pageId - 1]));
                }
                if (onPreviousPage && pageId - 1 >= 0) {
                    onPreviousPage(pageId - 1);
                }
            }, onFinishClick: () => {
                if (onFinish) {
                    onFinish();
                }
            }, onCancelClick: () => {
                if (onCancel !== undefined) {
                    onCancel();
                }
            }, onSaveClick: () => {
                if (onSave !== undefined) {
                    onSave();
                }
            }, leftAlignedButtons: leftAlignedButtons, rightAlignedButtons: rightAlignedButtons })));
};

const WizardColumn = ({ children, type, className, style, dataAttributes }) => (React.createElement(Column, { ...(dataAttributes && createDataAttributeObject(dataAttributes)), sm: 12, md: 6, lg: 4, className: cx(className, { notes: type === "note" }), style: style }, children));

const WizardRow = ({ children, className, style, dataAttributes }) => {
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("row", className), style: style }, children));
};

const WizardPage = ({ title, children, page, totalPages, className, style, dataAttributes }) => {
    const subtitle = page !== undefined && totalPages !== undefined ? (React.createElement("div", { className: "subtitle" },
        "Step",
        React.createElement("span", { style: { marginLeft: "2px", marginRight: "3px" }, className: "current" }, page),
        "of",
        React.createElement("span", { style: { marginLeft: "3px", marginRight: "3px" }, className: "total" }, totalPages))) : null;
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("content", className), style: style },
        React.createElement("div", { className: "title" }, title),
        subtitle,
        children));
};

const Card = (props) => {
    const [selected, setSelected] = useState(false);
    const { title, subTitle, headerActions, actions, content, className, selectable = true, disabled = false, draggable = false, style, dataAttributes } = { ...props };
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), role: "presentation", className: cx("card", { selected, disabled, draggable }, className), style: style, onClick: () => {
            if (selectable) {
                setSelected(prev => !prev);
            }
        } },
        React.createElement("div", { className: "header" },
            React.createElement("div", { className: "left" },
                React.createElement("div", { className: "title" }, title),
                subTitle && React.createElement("div", { className: "subtitle" }, subTitle)),
            headerActions && React.createElement("div", { className: "right" }, headerActions)),
        content && React.createElement("div", { className: "content" }, content),
        actions && React.createElement("p", null, actions)));
};

const useNotifications = () => {
    const { notifications, bannerNotifications } = useAppContext();
    return {
        createNotification: notifications.create,
        createInstantNotification: notifications.createInstant,
        createBannerNotification: bannerNotifications.create,
        createSilentNotification: notifications.createSilent,
        notificationLogVisible: notifications.visible,
        toggleNotificationLog: notifications.toggle,
        notificationItems: notifications.items,
        clearNotification: notifications.clear,
        deleteNotification: notifications.delete
    };
};

const useSettings = () => {
    const { theme, settings } = useAppContext();
    return {
        theme: theme.current,
        toggleSettings: settings.toggle,
        settingsVisible: settings.visible
    };
};

const Kpi = ({ children, className = "", style, dataAttributes }) => {
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("kpi", className), style: style }, children));
};

const KpiItem = ({ lg, xl, color, sm, inline, span, separator, className, children, dataAttributes }) => {
    const customColor = color ? `color-${color}` : "";
    const classes = cx({
        item: inline,
        "text-lg": lg,
        "text-xl": xl,
        "text-sm": sm
    }, className, customColor);
    const style = separator ? { marginLeft: "5px", marginRight: "5px" } : {};
    return !span ? (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: classes, style: style }, children)) : (React.createElement("span", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: classes }, children));
};

const KpiProgressBar = ({ value, red, className, style, dataAttributes }) => {
    return (React.createElement(ProgressBar, { dataAttributes: dataAttributes, className: className, style: style, max: 100, value: value, color: red ? "red" : "", thin: true }));
};

const KpiWarning = ({ label, className = "", style, dataAttributes }) => {
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: className, style: style },
        React.createElement("i", { className: "icon icon-triangle" }),
        React.createElement("span", { className: "text-sm" }, label)));
};

const KpiAction = ({ label, onClick, className = "", style, dataAttributes }) => {
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), role: "presentation", className: cx("color-gray action", className), style: style, onClick: () => onClick && onClick() },
        React.createElement("span", { className: "item" }, label),
        React.createElement("span", { className: "item color-blue" },
            React.createElement("i", { className: "icon icon-arrow-right" }))));
};

const RangeSlider = ({ min = 0, max = 100, value = 0, label, unit, tooltipLabels, step = 1, multiple, thresholds, values = [0, 0], hideTooltip = false, disabled, leftLabel, rightLabel, className, style, dataAttributes, handleStart, handleDrag, handleEnd }) => {
    const slider = useRef(null);
    const handle = useRef(null);
    const primaryRangerTooltip = useRef(null);
    const secondaryRangerTooltip = useRef(null);
    const [positions, setPositions] = useState([]);
    const [initialized, setInitialized] = useState(false);
    const [useCombinedTooltip, setUseCombinedTooltip] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [currentHandle, setCurrentHandle] = useState(-1);
    const [focus, setFocus] = useState(false);
    const containerRef = useMoveOutside(() => {
        setDragging(false);
    });
    useEffect(() => {
        const newPositions = [];
        let n = Math.abs(max);
        if (min < 0) {
            n += Math.abs(min);
        }
        else {
            n -= min;
        }
        const percentStep = 100 / n;
        if (step > 0) {
            for (let i = 0; i <= n; i += step) {
                newPositions.push({
                    value: min + i,
                    position: i * percentStep
                });
            }
        }
        setPositions(newPositions);
        setInitialized(true);
    }, [min, max, step]);
    const currentPositions = positions.filter((cur) => {
        if (multiple) {
            return cur.value === values[0] || cur.value === values[1];
        }
        return cur.value === value;
    });
    const getHandlePositions = () => {
        const rangerStyle = !multiple ? { width: "0%" } : { left: "0%", width: "0%" };
        const multipleHandlerStyling = currentPositions[1] !== undefined
            ? {
                left: `${currentPositions[0].position}%`,
                width: `${currentPositions[1].position - currentPositions[0].position}%`
            }
            : {
                width: "0%",
                left: `${currentPositions[0].position}%`
            };
        if (currentPositions !== undefined && currentPositions[0] !== undefined) {
            return !multiple ? { width: `${currentPositions[0].position}%` } : multipleHandlerStyling;
        }
        return rangerStyle;
    };
    const tipsIntersecting = () => {
        if (primaryRangerTooltip.current && secondaryRangerTooltip.current) {
            const a = primaryRangerTooltip.current.getBoundingClientRect();
            const b = secondaryRangerTooltip.current.getBoundingClientRect();
            return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
        }
        return false;
    };
    const getValueFromPosition = (position) => {
        const pos = position - (slider.current?.offsetLeft || 0);
        const size = slider.current?.offsetWidth;
        // get the position of the cursor over the bar as a percentage
        const newPosition = (pos / (size || 1)) * 100;
        const closest = positions.reduce((prev, curr) => {
            return Math.abs(curr.position - newPosition) < Math.abs(prev.position - newPosition)
                ? curr
                : prev;
        });
        const newValue = closest.value;
        if (multiple) {
            const newValues = [values[0], values[1]];
            if (currentHandle === -1) {
                if (newValue < values[0]) {
                    setCurrentHandle(0);
                }
                if (newValue > values[1]) {
                    setCurrentHandle(1);
                }
                if (newValue > values[0] && newValue < values[1]) {
                    if (Math.abs(newValue - values[0]) < Math.abs(newValue - values[1])) {
                        setCurrentHandle(0);
                    }
                    else {
                        setCurrentHandle(1);
                    }
                }
            }
            newValues[currentHandle] = newValue;
            setUseCombinedTooltip(tipsIntersecting());
            if (newValues[0] <= newValues[1]) {
                return newValues;
            }
            return values;
        }
        return closest.value;
    };
    if (!initialized) {
        return React.createElement("div", null);
    }
    const getLabels = () => {
        if (tooltipLabels) {
            const labelsDictionary = Object.keys(tooltipLabels);
            const foundLabels = ["", ""];
            labelsDictionary.forEach(key => {
                if (parseInt(key, 0) === values[0]) {
                    foundLabels[0] = tooltipLabels[key];
                }
                if (parseInt(key, 0) === values[1]) {
                    foundLabels[1] = tooltipLabels[key];
                }
                const nextIndex = labelsDictionary.indexOf(key) + 1;
                const nextKey = labelsDictionary[nextIndex] !== undefined
                    ? labelsDictionary[nextIndex]
                    : Number.MAX_SAFE_INTEGER.toString();
                if (values[0] > parseInt(key, 0) && values[0] < parseInt(nextKey, 0)) {
                    foundLabels[0] = tooltipLabels[key];
                }
                if (values[1] > parseInt(key, 0) && values[1] < parseInt(nextKey, 0)) {
                    foundLabels[1] = tooltipLabels[key];
                }
            });
            return foundLabels;
        }
        return values;
    };
    const getLabel = () => {
        if (tooltipLabels) {
            const labelsDictionary = Object.keys(tooltipLabels);
            let foundLabel = "";
            labelsDictionary.forEach(key => {
                if (parseInt(key, 0) === value) {
                    foundLabel = tooltipLabels[key];
                }
                const nextIndex = labelsDictionary.indexOf(key) + 1;
                const nextKey = labelsDictionary[nextIndex] !== undefined
                    ? labelsDictionary[nextIndex]
                    : Number.MAX_SAFE_INTEGER.toString();
                if (value > parseInt(key, 0) && value < parseInt(nextKey, 0)) {
                    foundLabel = tooltipLabels[key];
                }
            });
            return foundLabel;
        }
        return value.toString();
    };
    const tooltipLabel = !multiple ? getLabel() : getLabels();
    const getTooltipStyling = () => {
        const fontSize = 12;
        if (value === min) {
            return {
                paddingLeft: `${getStringDomLength(min.toString(), fontSize) / 2}px`
            };
        }
        if (value === max) {
            return {
                marginRight: `${getStringDomLength(max.toString(), fontSize) / 4}px`
            };
        }
        return { padding: 0 };
    };
    const handleKeyDown = (e) => {
        e.preventDefault();
        if (multiple || !handleDrag) {
            return;
        }
        const { key } = e;
        if (key === "ArrowUp" || key === "ArrowRight") {
            handleDrag(value + step > max ? max : value + step);
        }
        if (key === "ArrowLeft" || key === "ArrowDown") {
            handleDrag(value - step < min ? min : value - step);
        }
    };
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx({ disabled }, className), style: style, ref: containerRef, role: "presentation", onMouseDown: (e) => {
            setDragging(true);
            if (handleStart) {
                handleStart(getValueFromPosition(e.clientX - 15));
            }
            setFocus(true);
        }, onMouseMove: (e) => {
            if (dragging && handleDrag) {
                handleDrag(getValueFromPosition(e.clientX - 15));
            }
        }, onMouseUp: (e) => {
            if (handleEnd) {
                handleEnd(getValueFromPosition(e.clientX - 15));
            }
            setDragging(false);
            setFocus(false);
            setCurrentHandle(-1);
        } },
        label && React.createElement("label", { htmlFor: "range" }, label),
        React.createElement("div", { role: "presentation", className: cx("ranger-container", { disabled }, { "ranger-multiple": multiple }, { "ranger-thresholds": thresholds }, { "combined-tooltip": useCombinedTooltip }), onKeyDown: (e) => handleKeyDown(e), tabIndex: 0, ref: slider },
            React.createElement("div", { className: "ranger-track" },
                thresholds && (React.createElement("div", { className: "ranger-lower", style: { width: `${currentPositions[0].position}%` } })),
                React.createElement("div", { className: cx("ranger-progress", { focus }), style: getHandlePositions() },
                    React.createElement("div", { className: cx("ranger-handle", { focus }), ref: handle }, !hideTooltip && (React.createElement("div", { className: "ranger-tooltip unselectable", style: getTooltipStyling(), ref: primaryRangerTooltip }, !multiple ? tooltipLabel : tooltipLabel[0]))),
                    multiple && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: cx("ranger-handle", { focus }) }, !hideTooltip && (React.createElement("div", { className: "ranger-tooltip unselectable", style: { marginRight: "0px" }, ref: secondaryRangerTooltip }, values[1]))),
                        useCombinedTooltip && !hideTooltip && (React.createElement("div", { className: "ranger-tooltip unselectable", style: { marginRight: "0px;" } }, values[0] === values[1] ? values[0] : `${values[0]} - ${values[1]}`))))),
                React.createElement("div", { className: "range-limits" },
                    React.createElement("div", { className: "range-limit unselectable" },
                        leftLabel ?? min,
                        unit && React.createElement("div", { className: "ranger-units" }, unit)),
                    React.createElement("div", { className: "range-limit unselectable" },
                        rightLabel ?? max,
                        unit && React.createElement("div", { className: "ranger-units" }, unit)))))));
};

const EmptyState = ({ message, action, className, style, dataAttributes, children }) => {
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("empty-state", className), style: style },
        React.createElement("div", { className: "message" },
            children,
            message && React.createElement("p", null, message),
            action)));
};

const Calendar = ({ selectedDate, visibleDate, disabled, onDateSelected, onVisibleDateChanged, visible = true, className, style, dataAttributes }) => {
    const selectedOrNewDate = selectedDate ?? new Date();
    return (React.createElement(DateCalendar, { dataAttributes: dataAttributes, className: cx("calendar-no-overlay", className), style: style, disabled: disabled, visible: visible, visibleDate: visibleDate ?? selectedOrNewDate, selectedDate: selectedOrNewDate, onVisibleDateChanged: (date) => onVisibleDateChanged && onVisibleDateChanged(date), onDateSelected: (date) => onDateSelected && onDateSelected(date) }));
};

const Breadcrumb = ({ items, style, className, dataAttributes }) => {
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [highlightedItemIndex, setHighlightedItemIndex] = useState(-1);
    const containerRef = useClickOutside(() => setDropdownVisible(false));
    const handleMenuClick = useCallback(() => setDropdownVisible(prev => !prev), []);
    const handleItemClick = useCallback((index) => {
        setDropdownVisible(false);
        items[index]?.action?.();
    }, [items]);
    const handleContainerKeyPress = useCallback((e) => {
        const { key } = e;
        if (key === "Enter") {
            if (!dropdownVisible) {
                setDropdownVisible(true);
                return;
            }
            handleItemClick(highlightedItemIndex);
            return;
        }
        if (key === "ArrowUp") {
            setHighlightedItemIndex(highlightedItemIndex > 0 ? highlightedItemIndex - 1 : items.length - 1);
            return;
        }
        if (key === "ArrowDown") {
            setHighlightedItemIndex(highlightedItemIndex < items.length - 1 ? highlightedItemIndex + 1 : 0);
        }
    }, [dropdownVisible, handleItemClick, highlightedItemIndex, items.length]);
    const renderItem = useCallback((text, index, isLink) => (React.createElement("span", { role: "presentation", key: uniqueId(text), className: "link", onClick: isLink
            ? e => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                handleItemClick(index);
            }
            : undefined }, text)), [handleItemClick]);
    const isShort = useMemo(() => items.length < 4, [items.length]);
    const menu = useMemo(() => (React.createElement("span", { className: "more", ref: containerRef, tabIndex: 0, role: "button", onKeyDown: handleContainerKeyPress, key: "menu" },
        React.createElement("div", { role: "presentation", onClick: handleMenuClick },
            React.createElement("i", { className: "icon" })),
        React.createElement("div", { className: cx("menu overflow", { visible: dropdownVisible }) }, items.slice(0, items.length - 1).map((b, index) => {
            return (React.createElement("div", { key: `${index}-${b.title}`, role: "presentation", className: cx("item", {
                    selected: highlightedItemIndex === index
                }), onClick: e => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    handleItemClick(index);
                } }, b.title));
        })))), [
        containerRef,
        handleContainerKeyPress,
        handleMenuClick,
        dropdownVisible,
        items,
        highlightedItemIndex,
        handleItemClick
    ]);
    const breadcrumbs = useMemo(() => items.reduce((aggregate, current, index, originalArray) => {
        // for long breadcrumbs, we want to render only the first and two last items
        const shouldNotRenderCurrentItem = !isShort && !(index === 0 || index > originalArray.length - 3);
        if (shouldNotRenderCurrentItem)
            return aggregate;
        const newArray = [...aggregate];
        const shouldRenderMenuBeforeFirstItem = isShort && index === 0;
        if (shouldRenderMenuBeforeFirstItem) {
            newArray.push(menu);
        }
        const isNotLastItem = index < originalArray.length - 1;
        newArray.push(renderItem(current.title, index, isNotLastItem));
        if (isNotLastItem) {
            newArray.push(React.createElement("span", { key: `separator-${current.title}`, className: "separator" }));
        }
        const shouldRenderMenuAfterFirstItem = !isShort && index === 0;
        if (shouldRenderMenuAfterFirstItem) {
            newArray.push(menu);
            newArray.push(React.createElement("span", { key: "menu-separator", className: "separator" }));
        }
        return newArray;
    }, []), [isShort, items, menu, renderItem]);
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("breadcrumb", isShort ? "short" : "long", className), style: style }, breadcrumbs));
};

const Text = ({ size, as, children, style, dataAttributes, className = "" }) => {
    if (as === "span") {
        return (React.createElement("span", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: `text-${size || ""} ${className}`, style: style }, children));
    }
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: `text-${size || ""} ${className}`, style: style }, children));
};

/* Limits */
const getLowerLimit = (format) => {
    return format === "12" ? 1 : 0;
};
const getUpperLimit = (format) => {
    return format === "12" ? 12 : 23;
};
const convertLimit = (value, meridiem) => {
    if (meridiem === "AM" && value === 12)
        return 0;
    if (meridiem === "AM")
        return value;
    if (meridiem === "PM" && value === 12)
        return 12;
    return value + 12;
};
/* Parsing */
const parseInteger = (value) => {
    if (/^(0?0|0?[1-9]\d*)$/.test(value)) {
        return Number(value);
    }
    return NaN;
};
const parseMeridiem = (defaultMeridiem, time) => {
    if (!time || time.length !== 4) {
        return defaultMeridiem;
    }
    const meridiem = time.slice(2, 4);
    if (meridiem !== "AM" && meridiem !== "PM") {
        return defaultMeridiem;
    }
    return meridiem;
};
const parseMin = (format, defaultMin, time) => {
    // 12 hours
    if (format === "12") {
        if (!time || time.length !== 4) {
            return defaultMin;
        }
        const min = parseInteger(time.slice(0, 2));
        if (!min || min < 1 || min > 12) {
            return defaultMin;
        }
        const meridiem = parseMeridiem("", time);
        if (!meridiem) {
            return defaultMin;
        }
        // Convert limits to 24 hours to deal with edge cases
        return convertLimit(min, meridiem);
    }
    // 24 hours
    if (!time || time.length !== 2) {
        return defaultMin;
    }
    const min = parseInteger(time);
    if (!min || min < 0 || min > 23) {
        return defaultMin;
    }
    return min;
};
const parseMax = (format, defaultMax, time) => {
    // 12 hours
    if (format === "12") {
        if (!time || time.length !== 4) {
            return defaultMax;
        }
        const max = parseInteger(time.slice(0, 2));
        if (!max || max < 1 || max > 12) {
            return defaultMax;
        }
        const meridiem = parseMeridiem("", time);
        if (!meridiem) {
            return defaultMax;
        }
        return convertLimit(max, meridiem);
    }
    // 24 hours
    if (!time || time.length !== 2) {
        return defaultMax;
    }
    const max = parseInteger(time);
    if (!max || max < 0 || max > 23) {
        return defaultMax;
    }
    return max;
};
/* String methods */
const timePad = (time) => {
    return time.toString().padStart(2, "0");
};
const timeToString = (format, hour, minute) => {
    if (format === "12") {
        const tempHour = hour > 12 ? hour - 12 : hour;
        const hourString = timePad(tempHour);
        const minuteString = timePad(minute);
        const meridiemString = hour > 11 ? "PM" : "AM";
        return `${hourString}:${minuteString} ${meridiemString}`;
    }
    const hourString = timePad(hour);
    const minuteString = timePad(minute);
    return `${hourString}:${minuteString}`;
};
const listHours = (min, max) => {
    const hours = [];
    for (let i = min; i <= max; i += 1) {
        hours.push(i);
    }
    return hours;
};
const listMinutes = (step) => {
    const minutes = [];
    for (let i = 0; i < 60; i += step) {
        minutes.push(i);
    }
    return minutes;
};
const getHours = (format, min, max) => {
    const tempMin = parseMin(format, getLowerLimit(format), min);
    const tempMax = parseMax(format, getUpperLimit(format), max);
    const hours = listHours(tempMin, tempMax);
    const times = [];
    for (let i = 0; i < hours.length; i += 1) {
        times.push(timePad(hours[i]));
    }
    return times;
};
const getMinutes = (step) => {
    const tempStep = step ? parseInteger(step) : 5;
    const minutes = listMinutes(tempStep);
    const times = [];
    for (let i = 0; i < minutes.length; i += 1) {
        times.push(timePad(minutes[i]));
    }
    return times;
};
const getCombined = (format, step, min, max) => {
    const tempStep = step ? parseInteger(step) : 5;
    const minutes = listMinutes(tempStep);
    const combined = [];
    // 12 hours
    if (format === "12") {
        const tempMin = parseMin(format, 0, min);
        const tempMax = parseMax(format, 24, max);
        const hours = listHours(tempMin, tempMax - 1);
        for (let i = 0; i < hours.length; i += 1) {
            for (let j = 0; j < minutes.length; j += 1) {
                combined.push(timeToString(format, hours[i], minutes[j]));
            }
        }
        return combined;
    }
    // 24 hours
    const tempMin = parseMin(format, 0, min);
    const tempMax = parseMax(format, 24, max) - 1;
    const hours = listHours(tempMin, tempMax);
    for (let i = 0; i < hours.length; i += 1) {
        for (let j = 0; j < minutes.length; j += 1) {
            combined.push(timeToString(format, hours[i], minutes[j]));
        }
    }
    return combined;
};

const Timepicker = ({ value, meridiem, type, format, disabled, onChange, onHourChange, onMinuteChange, onMeridiemChange, min, max, step, disable, style, className, dataAttributes }) => {
    const isMeridiem = format === "12";
    const combinedPlaceholder = isMeridiem ? "HH:MM AM" : "HH:MM";
    const combinedDisabled = disable ? disable.split(", ") : [];
    // Opening and closing menus
    const [hourOpen, setHourOpen] = useState(false);
    const [minuteOpen, setMinuteOpen] = useState(false);
    const [meridiemOpen, setMeridiemOpen] = useState(false);
    const [combinedOpen, setCombinedOpen] = useState(false);
    const containerHourRef = useClickOutside(() => setHourOpen(false));
    const containerMinuteRef = useClickOutside(() => setMinuteOpen(false));
    const containerMeridiemRef = useClickOutside(() => setMeridiemOpen(false));
    const containerCombinedRef = useClickOutside(() => setCombinedOpen(false));
    // Generate menu options
    const meridiemItems = ["AM", "PM"];
    const hourItems = getHours(format);
    const minuteItems = getMinutes(step);
    const combinedItems = [combinedPlaceholder, ...getCombined(format, step, min, max)];
    const combinedText = value;
    const hourText = value && value.includes(":") ? value.split(":")[0] : "";
    const minuteText = value && value.includes(":") ? value.split(":")[1] : "";
    const meridiemText = isMeridiem && meridiem ? meridiem : "";
    // Scrolling
    const hourRefs = hourItems.reduce((acc, invalue) => {
        acc[hourItems.indexOf(invalue)] = React.createRef();
        return acc;
    }, {});
    const minuteRefs = minuteItems.reduce((acc, invalue) => {
        acc[minuteItems.indexOf(invalue)] = React.createRef();
        return acc;
    }, {});
    // Highlighting
    const [hourHighlight, setHourHighlight] = useState(-1);
    const [minuteHighlight, setMinuteHighlight] = useState(-1);
    const findFirstMatch = (items, typedValue) => {
        if (!typedValue) {
            return -1;
        }
        for (let i = 0; i < items.length; i += 1) {
            if (items[i].includes(typedValue)) {
                return i;
            }
        }
        return -1;
    };
    useEffect(() => {
        const handleHourScroll = (id) => {
            return hourRefs[id].current.scrollIntoView({
                block: "start"
            });
        };
        if (type === "separate") {
            const index = findFirstMatch(hourItems, hourText);
            if (index !== -1) {
                setHourHighlight(index);
                handleHourScroll(index);
            }
            else {
                setHourHighlight(-1);
                handleHourScroll(0);
            }
        }
    }, [hourItems, hourRefs, hourText, type]);
    useEffect(() => {
        const handleMinuteScroll = (id) => {
            return minuteRefs[id].current.scrollIntoView({
                block: "start"
            });
        };
        if (type === "separate") {
            const index = findFirstMatch(minuteItems, minuteText);
            if (index !== -1) {
                setMinuteHighlight(index);
                handleMinuteScroll(index);
            }
            else {
                setMinuteHighlight(-1);
                handleMinuteScroll(0);
            }
        }
    }, [minuteItems, minuteRefs, minuteText, type]);
    const isOutOfViewport = (elem, items) => {
        const itemHeightOffset = 32;
        const maxOffset = 130;
        const offsetHeight = items.length < 4 ? itemHeightOffset * items.length : maxOffset;
        const bounding = elem.getBoundingClientRect();
        return (bounding.bottom + offsetHeight > (window.innerHeight || document.documentElement.clientHeight));
    };
    const outOfViewport = (ref, items) => {
        return ref.current ? isOutOfViewport(ref.current, items) : false;
    };
    const meridiemButton = isMeridiem ? (React.createElement("div", { className: cx("tp-interval-select tp-select", { open: meridiemOpen }, { closed: !meridiemOpen }, { "open-top": outOfViewport(containerMeridiemRef, meridiemItems) }), ref: containerMeridiemRef, role: "presentation", onClick: e => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setMeridiemOpen(!meridiemOpen);
        } },
        React.createElement("button", { type: "button", className: "btn tp-select-current" }, meridiemText),
        React.createElement("div", { className: "tp-select-options" }, meridiemItems.map((item) => (React.createElement("div", { key: item, title: item, role: "presentation", onClick: e => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                if (onMeridiemChange !== undefined) {
                    onMeridiemChange(item);
                }
                setMeridiemOpen(false);
            }, className: cx("tp-select-option", {
                active: item === meridiemText
            }) }, item)))))) : null;
    const hourButton = (React.createElement("div", { className: cx("tp-select tp-hours-select", { open: hourOpen }, { closed: !hourOpen }, { "open-top": outOfViewport(containerHourRef, hourItems) }), ref: containerHourRef, role: "presentation", onClick: e => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setHourOpen(!hourOpen);
        } },
        React.createElement("input", { value: hourText || "", type: "text", maxLength: 2, className: "tp-select-current tp-hh-input", placeholder: "HH", onChange: (e) => {
                if (e.target.value === "") {
                    setHourOpen(true);
                }
                if (onHourChange) {
                    onHourChange(e.target.value);
                }
            } }),
        React.createElement("div", { className: "tp-select-options" }, hourItems.map((item, index) => (React.createElement("div", { ref: hourRefs[index], key: item, title: item, role: "presentation", onClick: e => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                if (onHourChange !== undefined) {
                    onHourChange(item);
                }
                setHourOpen(false);
            }, className: cx("tp-select-option", {
                active: index === hourHighlight
            }) }, item))))));
    const minuteButton = (React.createElement("div", { className: cx("tp-select tp-minutes-select", { open: minuteOpen }, { closed: !minuteOpen }, { "open-top": outOfViewport(containerMinuteRef, minuteItems) }), ref: containerMinuteRef, role: "presentation", onClick: e => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setMinuteOpen(!minuteOpen);
        } },
        React.createElement("input", { value: minuteText || "", type: "text", maxLength: 2, className: "tp-select-current tp-mm-input", placeholder: "MM", onChange: (e) => {
                if (e.target.value === "") {
                    setMinuteOpen(true);
                }
                if (onMinuteChange) {
                    onMinuteChange(e.target.value);
                }
            } }),
        React.createElement("div", { className: "tp-select-options" }, minuteItems.map((item, index) => {
            return (React.createElement("div", { ref: minuteRefs[index], key: item, title: item, role: "presentation", onClick: e => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    if (onMinuteChange !== undefined) {
                        onMinuteChange(item);
                    }
                    setMinuteOpen(false);
                }, className: cx("tp-select-option", {
                    active: index === minuteHighlight
                }) }, item));
        }))));
    if (type === "separate") {
        return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("time-picker", { disabled }) },
            hourButton,
            React.createElement("span", { className: "tp-col-sep" }, ":"),
            minuteButton,
            meridiemButton));
    }
    if (type === "combined") {
        return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("time-picker", { disabled }) },
            React.createElement("div", { className: cx("tp-single-select tp-select", `time-format-${format}`, { open: combinedOpen }, { closed: !combinedOpen }), ref: containerCombinedRef, role: "presentation", onClick: e => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    setCombinedOpen(!combinedOpen);
                } },
                React.createElement("button", { type: "button", className: "btn tp-select-current" }, combinedText || combinedPlaceholder),
                React.createElement("div", { className: "tp-select-options" }, combinedItems.map((item) => {
                    return (React.createElement("div", { key: item, title: item, role: "presentation", onClick: e => {
                            e.stopPropagation();
                            e.nativeEvent.stopImmediatePropagation();
                            if (onChange !== undefined) {
                                onChange(item);
                            }
                            setCombinedOpen(false);
                        }, className: cx("tp-select-option", { active: item === combinedText }, { disabled: combinedDisabled.includes(item) }) }, item));
                })))));
    }
    if (type === "time") {
        return (React.createElement("input", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), type: "time", value: value, style: style, className: className, onChange: (e) => e.target.value && onChange && onChange(e.target.value) }));
    }
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: "time-picker invalid-type", style: style }, "Invalid type"));
};

const TimeLine = ({ horizontal, compact, timeline, locale = "en-Us", datesAsHeadings, collapsible, onEntryClick, className, style, dataAttributes }) => {
    const [selectedEntry, setSelectedEntry] = useState();
    const [collapsedEntries, setCollapsedEntries] = useState({});
    const [headings, setHeadings] = useState([]);
    useEffect(() => {
        setSelectedEntry(undefined);
    }, [timeline]);
    useEffect(() => {
        if (datesAsHeadings && timeline) {
            const headers = [];
            timeline.forEach(t => {
                const p = t.time.toLocaleDateString(locale);
                if (headers.indexOf(p) < 0) {
                    headers.push(p);
                }
            });
            setHeadings(headers);
        }
    }, [datesAsHeadings, locale, timeline]);
    const toggleCollapsed = (key) => {
        setCollapsedEntries(state => ({ ...state, [key]: !state[key] }));
    };
    const renderEntry = (e, key) => {
        return (React.createElement("li", { key: `entry_${key}`, className: cx("entry", e.icon && `icon-${e.icon}`, key === selectedEntry && "active", collapsedEntries[key] && "collapsed"), onClick: () => {
                setSelectedEntry(key);
                onEntryClick && onEntryClick(e);
            } },
            React.createElement("div", { className: "target" },
                e.time && (React.createElement("time", { className: "time" }, e.omitTimestamp
                    ? e.time.toLocaleDateString(locale)
                    : e.time.toLocaleString(locale))),
                React.createElement("h4", { className: "title" }, e.title),
                e.status && (React.createElement("span", { className: "status" }, e.status.map((s, index) => {
                    return (React.createElement("span", { key: index, className: cx(`${s.icon}`) }, s.label));
                }))),
                React.createElement("div", { className: "content" }, e.content)),
            collapsible && (React.createElement("button", { className: "collapse-btn", onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleCollapsed(key);
                } }))));
    };
    const renderNormal = () => {
        return (React.createElement("ul", { className: cx("main-list", onEntryClick && "selectable") }, timeline?.map((e, index) => {
            return renderEntry(e, index.toString());
        })));
    };
    const renderWithHeaders = () => {
        return (React.createElement("ul", { className: cx("main-list headings", onEntryClick && "selectable") }, headings.map(h => {
            return (React.createElement("li", { className: "date-group", key: h },
                React.createElement("div", { className: "heading" },
                    React.createElement("span", { className: "label" }, h)),
                React.createElement("ul", { className: "entry-list" }, timeline
                    // @ts-ignore: This is invalid typescript
                    .filter((e) => {
                    return e.time.toLocaleDateString(locale) === h;
                })
                    ?.map((e, index) => {
                    return renderEntry(e, `${h}_${index}`);
                }))));
        })));
    };
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), style: style, className: cx("timeline", horizontal && "horizontal", compact && "compact", className) }, datesAsHeadings ? renderWithHeaders() : renderNormal()));
};

const Carousel = ({ slides, onLeftClick, onRightClick, current, onClick }) => {
    return (React.createElement("div", { className: "carousel-container", style: {
            height: "300px"
        } },
        React.createElement("div", { className: "carousel-wrapper" }, slides),
        current !== undefined && (React.createElement("div", { className: "carousel-counter" },
            current + 1,
            "/",
            React.Children.count(slides))),
        React.createElement(Button, { className: "carousel-button-prev", onClick: onLeftClick, iconName: "chevron-left" }),
        React.createElement(Button, { className: "carousel-button-next", onClick: onRightClick, iconName: "chevron-right" }),
        React.createElement("div", { className: "carousel-pagination" },
            React.createElement("div", { className: "carousel-pagination-wrap" }, 
            // @ts-ignore
            React.Children.map(slides, (slide, idx) => {
                return (React.createElement("span", { className: `carousel-pagination-bullet clickable ${idx === current ? "active" : ""}`, onClick: () => onClick && onClick(idx) }));
            })))));
};

const Slide = ({ children, active }) => {
    return React.createElement("div", { className: `carousel-slide ${active ? "active" : ""}` }, children);
};

const Form = ({ title, subtitle, footer, children, className, dataAttributes, actionIcon }) => {
    return (React.createElement("form", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("eds-form", className) },
        (title || subtitle) && (React.createElement("div", { className: cx("form-header", { "action-icon": actionIcon }) },
            React.createElement("div", null,
                title && React.createElement("div", { className: "title" }, title),
                React.createElement("p", null, subtitle)),
            actionIcon)),
        React.createElement("div", { className: "row form-body" }, children),
        React.createElement("div", { className: "form-footer" }, footer)));
};

const FormElement = ({ children, className, dataAttributes }) => {
    return (React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("form-element", className) }, children));
};

const FormGroup = ({ title, children, subtitle, className, dataAttributes, sm, md, lg, xl }) => {
    if (sm || md || lg || xl) {
        return (React.createElement(Column, { sm: sm, lg: lg, md: md, xl: xl },
            React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("form-group", className) },
                title && React.createElement("div", { className: "form-group-title" }, title),
                React.createElement("p", null, subtitle),
                children)));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("form-group", className) },
            title && React.createElement("div", { className: "form-group-title" }, title),
            React.createElement("p", null, subtitle),
            children)));
};

const ListBuilder = ({ leftTitle, rightTitle, onLeftSearch, onRightSearch, leftSideItems, rightSideItems, buttons, disabled, onTransferLeftClick, onTransferRightClick, hideTransferLeftButton, hideTransferRightButton, hideTransferButtons }) => {
    const [selectedItemsLeft, setSelectedItemsLeft] = useState([]);
    const [selectedItemsRight, setSelectedItemsRight] = useState([]);
    useEffect(() => {
        setSelectedItemsLeft([]);
    }, [leftSideItems]);
    useEffect(() => {
        setSelectedItemsRight([]);
    }, [rightSideItems]);
    const listItem = (item, selectedItems, setSelectedItems) => {
        return (React.createElement(Checkbox, { disabled: disabled, onChange: (checked) => {
                if (checked) {
                    setSelectedItems(items => [...items, item]);
                }
                else {
                    setSelectedItems(items => items.filter(i => i.title !== item.title));
                }
            }, text: item.title, checked: selectedItems.map(i => i.title).includes(item.title) }));
    };
    const selectAllItems = (items, selectedItems, setSelectedItems) => {
        return (React.createElement(Checkbox, { disabled: disabled, key: "select-all", onChange: (checked) => {
                setSelectedItems(checked ? [...(items || [])] : []);
            }, checked: selectedItems.length === items?.length && items?.length > 0, text: "Select all items" }));
    };
    return (React.createElement("div", { className: "list-builder" },
        React.createElement("div", { className: "lb-block lb-main" },
            React.createElement("div", { className: "lb-header" },
                React.createElement("div", { className: `title ${disabled ? "disabled" : null}` }, leftTitle || React.createElement("span", null, "\u00A0")),
                React.createElement("div", { className: "lb-filter" },
                    React.createElement("input", { className: "lb-filter-input lb-filter-main", placeholder: "Type to search", type: "text", onChange: (e) => {
                            onLeftSearch && onLeftSearch(e.target.value);
                        }, disabled: disabled })),
                React.createElement("div", { className: "lb-controls" },
                    React.createElement("div", { className: "lb-header-cb" },
                        React.createElement("div", { className: "lb-primary-cb-all" }, leftSideItems?.length ? (React.createElement("div", { className: "lb-primary-cb-all" }, selectAllItems(leftSideItems, selectedItemsLeft, setSelectedItemsLeft))) : (React.createElement(React.Fragment, null)))),
                    React.createElement("div", { className: `lb_header-counters  ${disabled ? "disabled" : null}` },
                        React.createElement("span", { className: "lb-counter-total" },
                            leftSideItems ? leftSideItems.length : 0,
                            " |",
                            " "),
                        React.createElement("span", { className: "lb-counter-selected" }, selectedItemsLeft.length),
                        " selected"))),
            React.createElement("div", { className: "lb-list" },
                (!leftSideItems || !leftSideItems.length) && (React.createElement("p", { className: "lb-placeholder" }, "The list is empty.")),
                leftSideItems &&
                    leftSideItems.map((item, index) => {
                        return (React.createElement("div", { className: "lb-item", key: `${item.title}_${index}` }, listItem(item, selectedItemsLeft, setSelectedItemsLeft)));
                    }))),
        !hideTransferButtons && (React.createElement("div", { className: "lb-transfer-btns" },
            !hideTransferRightButton && (React.createElement(Button, { iconName: "chevron-right", onClick: () => {
                    onTransferRightClick && onTransferRightClick(selectedItemsLeft);
                    setSelectedItemsLeft([]);
                }, disabled: disabled })),
            !hideTransferLeftButton && (React.createElement(Button, { iconName: "chevron-left", onClick: () => {
                    onTransferLeftClick && onTransferLeftClick(selectedItemsRight);
                    setSelectedItemsRight([]);
                }, disabled: disabled })),
            buttons ?? buttons)),
        React.createElement("div", { className: `lb-block ${disabled ? "disabled" : undefined}` },
            React.createElement("div", { className: "lb-header" },
                React.createElement("div", { className: "title" }, rightTitle || React.createElement("span", null, "\u00A0")),
                React.createElement("div", { className: "lb-filter" },
                    React.createElement("input", { disabled: disabled, className: "lb-filter-input lb-filter-main", placeholder: "Type to search", type: "text", onChange: (e) => {
                            onRightSearch && onRightSearch(e.target.value);
                        } })),
                React.createElement("div", { className: "lb-controls" },
                    React.createElement("div", { className: "lb-header-cb" }, rightSideItems?.length ? (React.createElement("div", { className: "lb-primary-cb-all" }, selectAllItems(rightSideItems, selectedItemsRight, setSelectedItemsRight))) : (React.createElement(React.Fragment, null))),
                    React.createElement("div", { className: `lb_header-counters ${disabled ? "disabled" : undefined}` },
                        React.createElement("span", { className: "lb-counter-total" },
                            rightSideItems ? rightSideItems.length : 0,
                            " |",
                            " "),
                        React.createElement("span", { className: "lb-counter-selected" }, selectedItemsRight.length),
                        " selected"))),
            React.createElement("div", { className: "lb-list" },
                (!rightSideItems || !rightSideItems.length) && (React.createElement("p", { className: "lb-placeholder" }, "The list is empty.")),
                rightSideItems &&
                    rightSideItems.map((item, index) => {
                        return (React.createElement("div", { className: "lb-item", key: `${item.title}_${index}` }, listItem(item, selectedItemsRight, setSelectedItemsRight)));
                    })))));
};

const InlineNotification = ({ title, description, iconName, stripeColor, onClick, className, style, dataAttributes }) => {
    return (React.createElement("div", { className: `notification inline stripe-${stripeColor} ${className}`, style: style, ...(dataAttributes && createDataAttributeObject(dataAttributes)) },
        React.createElement("div", { className: "top-row" },
            React.createElement("div", { className: "title" },
                iconName && React.createElement(Icon, { name: iconName }),
                title),
            React.createElement(Icon, { name: "cross", className: "close", onClick: onClick })),
        description && (React.createElement("div", { className: "description" },
            React.createElement("p", null, description)))));
};

const SyntaxHighlight = ({ code, lineNumbers, dataAttributes }) => {
    const renderLineNumbers = () => {
        const lineBreaks = code.split("\n").length;
        const lines = Array.from(Array(lineBreaks).keys());
        return (React.createElement("span", { "aria-hidden": "true", className: "line-numbers-rows" }, lines.map((line) => (React.createElement("span", { key: line })))));
    };
    return (React.createElement("pre", { ...(dataAttributes && createDataAttributeObject(dataAttributes)), className: cx("language-x", { "line-numbers": lineNumbers }) },
        React.createElement("code", { className: "language-x" },
            code,
            lineNumbers && renderLineNumbers())));
};

export { Accordion, AccordionItem, AppBarAnchor, AppBarItem, ApplicationLayer, Breadcrumb, Button, ButtonGroup, Calendar, Card, Carousel, Checkbox, CheckboxGroup, Column, DateField, Dialog, Dropdown, DropdownMenu, EmptyState, FileUpload, Form, FormElement, FormGroup, GlobalNavigation, Icon, InlineNotification, Kpi, KpiAction, KpiItem, KpiProgressBar, KpiWarning, ListBuilder, Loader, Menu, MenuItem, MenuNode, MultiPanelTile, MultiSelect, NumberField, Pagination, PersistentBanner, Pill, ProgressBar, Radiobutton, RangeSlider, Row, SettingsItem, SettingsNode, SignIn, SingleSelect, Slide, Splash, Switch, SyntaxHighlight, SystemLayer, Tab, Table, Tabs, Tag, Text, TextArea, TextField, Tile, TileActionButton, TimeLine, Timepicker, Tooltip, Tree, TreeItem, TreeNode, Welcome, Wizard, WizardColumn, WizardPage, WizardRow, useApplication, useNotifications, useSettings };
